/**
 * 素材图层数据管理工具函数
 * 提供创建和管理素材图层数据结构的工具函数
 * 数据结构定义参考：doc/editPaster/data.md
 */
import Toast from '../components/Toast';
import * as constant from './constant.js';
import slog from './slog.js';
import * as mwx from './common.js';
import { Cache } from './Cache.js';
import * as canvasUtil from './canvasUtil.js';
import {
  convertGrayToAlphaForBackground,
  mirrorImageHorizontal
} from './canvasUtil.js';
import { imageWithWhiteInkDefaultOpacity } from './config.js';
import {
  Control,
  controlsUtils,
  Image as FabricImage,
  Point,
  StaticCanvas,
  Pattern,
  Rect
} from 'fabric';
import iconMoveUrl from '@/assets/image/icon_rotate.png';
import iconDeleteUrl from '@/assets/image/sticker/icon_delete.png';
import iconCopyUrl from '@/assets/image/sticker/icon_copy.png';
import iconScaleUrl from '@/assets/image/sticker/icon_scale.png';
import {
  setActiveObjectPinchGestureEnabled,
  enableActiveObjectPinchGesture,
  disableActiveObjectPinchGesture
} from './enableActiveObjectPinchGesture.js';

export {
  setActiveObjectPinchGestureEnabled,
  enableActiveObjectPinchGesture,
  disableActiveObjectPinchGesture
} from './enableActiveObjectPinchGesture.js';

const backgroundMaxZIndex = constant.backgroundMaxZIndex;
const maxLayerZIndex = constant.maxLayerZIndex;
const emptyWhiteInk = constant.emptyWhiteInk;
// 透明网格底图：固定放到最底层
const transparentGridZIndex = constant.transparentGridZIndex;
const transparentGridPatternSize = 400;

// 创建图片缓存实例，最多缓存10张图片
const imageCache = new Cache(10);

// 透明 PNG 的 blobUrl（按尺寸缓存复用）
const transparentBlobUrlBySizeCache = new Cache(20);
const transparentBlobUrlPromiseBySize = {};

/**
 * 创建离屏 StaticCanvas（用于导出/合成中间结果，避免污染主画布 UI）
 * @param {Object} options
 * @param {number} options.width
 * @param {number} options.height
 * @returns {fabric.StaticCanvas}
 */
function createOffscreenStaticCanvas(options) {
  const opts = options || {};
  const width = typeof opts.width === 'number' ? opts.width : 0;
  const height = typeof opts.height === 'number' ? opts.height : 0;
  if (!(width > 0 && height > 0)) {
    throw new Error('createOffscreenStaticCanvas: width/height 无效');
  }

  if (typeof document === 'undefined') {
    throw new Error('createOffscreenStaticCanvas: document 不存在');
  }

  const canvasEl = document.createElement('canvas');
  canvasEl.width = width;
  canvasEl.height = height;

  const offscreenCanvas = new StaticCanvas(canvasEl, {
    width: width,
    height: height
  });

  // 避免 add/remove 时频繁重绘；最终由调用方 toDataURL / renderAll 控制
  offscreenCanvas.renderOnAddRemove = false;

  return offscreenCanvas;
}

/**
 * 获取指定尺寸的全透明 PNG 的 blobUrl（生成并缓存）
 * - 用于“清空模板”时设置 templateLayer.imageUrl
 * - 缓存 key：`${width}x${height}`
 * - 注意：不 revoke，避免后续引用失效（生命周期跟随页面）
 * @param {Object} options
 * @param {number} options.width
 * @param {number} options.height
 * @returns {Promise<string>}
 */
export async function getTransparentBlobUrlBySize(options) {
  const opts = options || {};
  const width = typeof opts.width === 'number' ? opts.width : 0;
  const height = typeof opts.height === 'number' ? opts.height : 0;
  if (!(width > 0 && height > 0)) {
    throw new Error('getTransparentBlobUrlBySize: width/height 无效');
  }

  const cacheKey = `${width}x${height}`;
  const cached = transparentBlobUrlBySizeCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  if (transparentBlobUrlPromiseBySize[cacheKey]) {
    return await transparentBlobUrlPromiseBySize[cacheKey];
  }

  transparentBlobUrlPromiseBySize[cacheKey] = new Promise((resolve, reject) => {
    try {
      if (typeof document === 'undefined') {
        reject(new Error('getTransparentBlobUrlBySize: document 不存在'));
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, width, height);
      }

      if (typeof canvas.toBlob === 'function') {
        canvas.toBlob(
          (blob) => {
            try {
              if (!blob) {
                reject(
                  new Error('getTransparentBlobUrlBySize: toBlob 结果为空')
                );
                return;
              }
              if (
                typeof URL === 'undefined' ||
                typeof URL.createObjectURL !== 'function'
              ) {
                reject(
                  new Error(
                    'getTransparentBlobUrlBySize: URL.createObjectURL 不可用'
                  )
                );
                return;
              }
              const blobUrl = URL.createObjectURL(blob);
              transparentBlobUrlBySizeCache.set(cacheKey, blobUrl);
              resolve(blobUrl);
            } catch (error) {
              reject(error);
            }
          },
          'image/png',
          1
        );
        return;
      }

      // 兼容：没有 toBlob 时，退化为 dataURL -> Blob
      const dataUrl = canvas.toDataURL('image/png');
      const parts = dataUrl.split(',');
      if (!parts || parts.length < 2) {
        reject(new Error('getTransparentBlobUrlBySize: dataURL 格式异常'));
        return;
      }
      const header = parts[0];
      const rawBase64 = parts[1];
      const match = header.match(/:(.*?);/);
      const contentType = match && match[1] ? match[1] : 'image/png';

      // atob
      const byteCharacters = atob(rawBase64);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(null)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });

      if (
        typeof URL === 'undefined' ||
        typeof URL.createObjectURL !== 'function'
      ) {
        reject(
          new Error('getTransparentBlobUrlBySize: URL.createObjectURL 不可用')
        );
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      transparentBlobUrlBySizeCache.set(cacheKey, blobUrl);
      resolve(blobUrl);
    } catch (error) {
      reject(error);
    }
  }).finally(() => {
    delete transparentBlobUrlPromiseBySize[cacheKey];
  });

  return await transparentBlobUrlPromiseBySize[cacheKey];
}

/**
 * 兼容旧调用：1x1 透明 blobUrl
 * @returns {Promise<string>}
 */
export async function getTransparent1x1BlobUrl() {
  return await getTransparentBlobUrlBySize({ width: 1, height: 1 });
}

const noWhiteLayerType = [
  constant.layer_background,
  constant.layer_transparentGrid
];

/**
 * 判断是否为“透明网格底图”图层
 * @param {Object} layer - layer 对象
 * @returns {boolean} 是否为透明网格图层
 */
function isTransparentGridLayer(layer) {
  return layer && layer.type === constant.layer_transparentGrid;
}

/**
 * 获取透明网格底图资源信息
 * - imageUrl 使用 require 让 webpack 正确处理静态资源
 * - width/height 为透明网格图片的像素尺寸（用于计算缩放）
 *
 * @returns {{imageUrl: string, width: number, height: number}} 透明网格图片信息
 */
function getTransparentGridImageInfo() {
  // 使用 require 保持与现有代码一致（webpack 会处理静态资源）
  return {
    imageUrl: require('@/assets/image/transparent.png'),
    width: 376, // 图片的宽度
    height: 376
  };
}

/**
 * 创建透明网格底图 layer（仅用于编辑 UI 展示，不参与导出合成）
 * - zIndex 固定为最底层（transparentGridZIndex）
 * - layerInfo 仅用于标记资源信息，实际平铺尺寸在 Pattern 绘制时统一控制
 *
 * @param {Object} canvasInfo - 画布信息（至少包含 displayWidth/displayHeight）
 * @param {number} canvasInfo.displayWidth - 显示宽度
 * @param {number} canvasInfo.displayHeight - 显示高度
 * @returns {Object} 透明网格 layer 对象
 */
export function createTransparentGridLayer(canvasInfo) {
  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    throw new Error('createTransparentGridLayer: canvasInfo 参数不完整');
  }

  const transparentGridImageInfo = getTransparentGridImageInfo();
  let scale = canvasInfo.displayWidth / transparentGridImageInfo.width;

  return createLayer({
    type: constant.layer_transparentGrid,
    imageUrl: transparentGridImageInfo.imageUrl,
    layerInfo: createLayerInfo({
      left: 0,
      top: 0,
      width: transparentGridImageInfo.width,
      height: transparentGridImageInfo.height,
      scaleX: scale,
      scaleY: scale,
      angle: 0,
      originX: 'left',
      originY: 'top'
    }),
    opacity: 1,
    canEditImage: false,
    canAdjustLayer: false,
    zIndex: transparentGridZIndex
  });
}

/**
 * 确保 layers 中包含“透明网格底图”layer
 * - 若已存在则原样返回
 * - 若不存在则在数组最前插入（最终仍按 zIndex 排序渲染）
 * - 当 canvasInfo 不完整时不注入，避免影响调用方
 *
 * @param {Array} layers - layer 数组
 * @param {Object} canvasInfo - 画布信息（至少包含 displayWidth/displayHeight）
 * @returns {Array} 可能被注入过的 layer 数组
 */
function ensureTransparentGridLayer(layers, canvasInfo) {
  if (!layers || !Array.isArray(layers)) {
    return layers;
  }
  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    return layers;
  }
  const existed = layers.some((l) => isTransparentGridLayer(l));
  if (existed) {
    return layers;
  }
  const gridLayer = createTransparentGridLayer(canvasInfo);
  return [gridLayer].concat(layers);
}

/**
 * 在 Fabric canvas 中加载“透明网格底图”layer（Pattern + Rect 平铺）
 * - 图片先缩放到统一贴图尺寸，再做 pattern 平铺
 * - 设置 excludeFromExport=true，确保 Fabric 导出不带入网格
 *
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {Object} options.layer - 透明网格 layer 对象
 * @param {number} [options.scale=1] - 缩放比例（displayWidth -> 当前 canvas 尺寸）
 * @returns {Promise<fabric.Rect>} 返回 Rect 对象（不在此处添加到 canvas，由调用方统一 add/render）
 */
async function loadTransparentGridLayerToFabricCanvas(options) {
  const {
    fabricCanvas,
    layer,
    scale = 1,
    offsetX = 0,
    offsetY = 0,
    renderWidth,
    renderHeight
  } = options || {};

  if (!fabricCanvas) {
    throw new Error(
      'loadTransparentGridLayerToFabricCanvas: fabricCanvas 参数不能为空'
    );
  }
  if (!layer || !layer.imageUrl) {
    throw new Error('loadTransparentGridLayerToFabricCanvas: layer 参数无效');
  }

  // 读取透明度（从 layer.opacity 读取，默认为 1）
  const opacity = typeof layer.opacity === 'number' ? layer.opacity : 1;

  // 获取透明网格需要覆盖的宽高；默认覆盖整个 canvas
  const canvasWidth =
    typeof renderWidth === 'number' && renderWidth > 0
      ? renderWidth
      : typeof fabricCanvas.getWidth === 'function'
      ? fabricCanvas.getWidth()
      : fabricCanvas.width;
  const canvasHeight =
    typeof renderHeight === 'number' && renderHeight > 0
      ? renderHeight
      : typeof fabricCanvas.getHeight === 'function'
      ? fabricCanvas.getHeight()
      : fabricCanvas.height;

  if (
    typeof canvasWidth !== 'number' ||
    canvasWidth <= 0 ||
    typeof canvasHeight !== 'number' ||
    canvasHeight <= 0
  ) {
    throw new Error(
      'loadTransparentGridLayerToFabricCanvas: 无法获取有效的 canvas 尺寸'
    );
  }

  const patternImg = await FabricImage.fromURL(layer.imageUrl, {
    crossOrigin: 'anonymous'
  });
  const imgElement = patternImg.getElement();
  let patternSource = imgElement;
  if (typeof document !== 'undefined') {
    const patternCanvas = document.createElement('canvas');
    patternCanvas.width = transparentGridPatternSize;
    patternCanvas.height = transparentGridPatternSize;
    const patternCtx = patternCanvas.getContext('2d');
    if (patternCtx) {
      patternCtx.drawImage(
        imgElement,
        0,
        0,
        transparentGridPatternSize,
        transparentGridPatternSize
      );
      patternSource = patternCanvas;
    }
  }
  const pattern = new Pattern({
    source: patternSource,
    repeat: 'repeat'
  });

  const rect = new Rect({
    left: offsetX,
    top: offsetY,
    width: canvasWidth,
    height: canvasHeight,
    fill: pattern,
    opacity: opacity,
    selectable: false,
    hasControls: false,
    hasBorders: false,
    evented: false,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    excludeFromExport: true
  });

  // 存储 layerId
  rect.set('layerId', layer.id);
  rect.setCoords();
  return rect;
}

// ==============================
// layerInfo 图片尺寸校验（开发辅助，非阻塞）
// ==============================
// 是否开启 layerInfo.width/height 与 imageUrl 对应图片真实宽高一致性的校验
// - 默认：开发环境 mwx.isDev 时开启
// - 可覆盖：window.__enableLayerInfoImageSizeValidate__ = true/false
const ENABLE_LAYERINFO_IMAGE_SIZE_VALIDATE = function () {
  return process.env.NODE_ENV === 'development';
};

// 用于去重错误日志，避免拖拽/频繁更新时刷屏
const layerInfoImageSizeValidateLogCache = new Cache(200);

function validateLayerInfoImageSizeNonBlocking(layer, source) {
  try {
    if (!ENABLE_LAYERINFO_IMAGE_SIZE_VALIDATE()) {
      return;
    }

    if (!layer || typeof layer !== 'object') {
      return;
    }

    if ([constant.layer_transparentGrid].indexOf(layer.type) !== -1) {
      return;
    }

    const imageUrl = layer.imageUrl;
    if (!imageUrl || typeof imageUrl !== 'string') {
      return;
    }

    const layerInfo = layer.layerInfo;
    const layerId = layer.id || '';
    const layerType = layer.type || '';
    const src = source || '';

    if (!layerInfo || typeof layerInfo !== 'object') {
      const key = `layerInfoImageSizeValidate:missingLayerInfo:${layerId}:${layerType}:${imageUrl}:${src}`;
      if (!layerInfoImageSizeValidateLogCache.get(key)) {
        layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
        console.error('[layerInfoImageSizeValidate] layerInfo 缺失', {
          id: layerId,
          type: layerType,
          imageUrl: imageUrl,
          source: src
        });
      }
      return;
    }

    const w = layerInfo.width;
    const h = layerInfo.height;

    if (typeof w !== 'number' || typeof h !== 'number' || w <= 0 || h <= 0) {
      const key = `layerInfoImageSizeValidate:invalidLayerInfoWH:${layerId}:${layerType}:${imageUrl}:${src}`;
      if (!layerInfoImageSizeValidateLogCache.get(key)) {
        layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
        console.error(
          '[layerInfoImageSizeValidate] layerInfo.width/height 非法',
          {
            id: layerId,
            type: layerType,
            imageUrl: imageUrl,
            layerInfoWidth: w,
            layerInfoHeight: h,
            source: src
          }
        );
      }
      return;
    }

    // 异步校验，不阻塞主流程
    (async function () {
      try {
        const img = await mwx.getImageInfo(imageUrl);
        const imgW =
          img && typeof img.naturalWidth === 'number' && img.naturalWidth > 0
            ? img.naturalWidth
            : img && typeof img.width === 'number'
            ? img.width
            : 0;
        const imgH =
          img && typeof img.naturalHeight === 'number' && img.naturalHeight > 0
            ? img.naturalHeight
            : img && typeof img.height === 'number'
            ? img.height
            : 0;

        let isSkip = imgW == 1 && imgH == 1; // 清空背景时会生成1*1px的图片作为占位图(getTransparent1x1BlobUrl)
        if (isSkip) {
          return;
        }

        if (!(imgW > 0 && imgH > 0)) {
          const key = `layerInfoImageSizeValidate:invalidImageWH:${layerId}:${layerType}:${imageUrl}:${src}`;
          if (!layerInfoImageSizeValidateLogCache.get(key)) {
            layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
            console.error('[layerInfoImageSizeValidate] 获取图片宽高失败', {
              id: layerId,
              type: layerType,
              imageUrl: imageUrl,
              imageWidth: imgW,
              imageHeight: imgH,
              source: src
            });
          }
          return;
        }

        if (w !== imgW || h !== imgH) {
          const key = `layerInfoImageSizeValidate:mismatch:${layerId}:${layerType}:${imageUrl}:${w}x${h}:${imgW}x${imgH}:${src}`;
          if (!layerInfoImageSizeValidateLogCache.get(key)) {
            layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
            console.error(
              '[layerInfoImageSizeValidate] layerInfo.width/height 与 imageUrl 图片真实宽高不一致',
              {
                id: layerId,
                type: layerType,
                imageUrl: imageUrl,
                layerInfoWidth: w,
                layerInfoHeight: h,
                imageWidth: imgW,
                imageHeight: imgH,
                source: src
              }
            );
          }
        }
      } catch (error) {
        const key = `layerInfoImageSizeValidate:error:${layerId}:${layerType}:${imageUrl}:${src}`;
        if (!layerInfoImageSizeValidateLogCache.get(key)) {
          layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
          console.error('[layerInfoImageSizeValidate] 校验异常', {
            id: layerId,
            type: layerType,
            imageUrl: imageUrl,
            source: src,
            error: error
          });
        }
      }
    })();
  } catch (e) {
    // ignore
  }
}

// ==============================
// Fabric 控件定制（editImageNew 用）
// ==============================
const EDIT_IMAGE_CONTROL_COLOR = '#E60012';
const EDIT_IMAGE_BORDER_DASH_ARRAY = [6, 6];
const EDIT_IMAGE_BORDER_SCALE_FACTOR = 2;
// 缩放角点（tl/bl/br）矩形大小（px）
const EDIT_IMAGE_SCALE_CORNER_SIZE = 6;
// 旋转图标大小（px）
const EDIT_IMAGE_ROTATE_ICON_SIZE = 36; // 参考 0.2rem（1rem=100px）
// 旋转图标默认所在角（可选：tl / tr / bl / br）
let editImageRotateCorner = 'tr';

function normalizeEditImageRotateCorner(corner) {
  const c = typeof corner === 'string' ? corner.toLowerCase() : '';
  if (c === 'tl' || c === 'tr' || c === 'bl' || c === 'br') {
    return c;
  }
  return 'tr';
}

function getEditImageCornerXY(corner) {
  const c = normalizeEditImageRotateCorner(corner);
  if (c === 'tl') {
    return { x: -0.5, y: -0.5 };
  }
  if (c === 'tr') {
    return { x: 0.5, y: -0.5 };
  }
  if (c === 'bl') {
    return { x: -0.5, y: 0.5 };
  }
  return { x: 0.5, y: 0.5 };
}

/**
 * 设置 editImageNew 的旋转图标所在角（默认 tr）
 * @param {'tl'|'tr'|'bl'|'br'} corner
 */
export function setEditImageRotateCorner(corner) {
  editImageRotateCorner = normalizeEditImageRotateCorner(corner);
}

let cachedMoveIconImage = null;
let cachedMoveIconBinded = false;

function ensureMoveIconImageForCanvas(fabricObject) {
  // 在非浏览器环境（如 node）避免报错
  if (typeof Image === 'undefined') {
    return null;
  }

  if (!cachedMoveIconImage) {
    cachedMoveIconImage = new Image();
    cachedMoveIconImage.src = iconMoveUrl;
  }

  // 绑定一次 onload，加载完后触发一次重绘（尽量保证旋转图标能及时出现）
  if (!cachedMoveIconBinded) {
    cachedMoveIconBinded = true;
    cachedMoveIconImage.onload = function () {
      try {
        if (
          fabricObject &&
          fabricObject.canvas &&
          typeof fabricObject.canvas.requestRenderAll === 'function'
        ) {
          fabricObject.canvas.requestRenderAll();
        } else if (
          fabricObject &&
          fabricObject.canvas &&
          typeof fabricObject.canvas.renderAll === 'function'
        ) {
          fabricObject.canvas.renderAll();
        }
      } catch (e) {
        // ignore
      }
    };
  }

  return cachedMoveIconImage;
}

function renderMoveIconControl(ctx, left, top, styleOverride, fabricObject) {
  const img = ensureMoveIconImageForCanvas(fabricObject);
  if (!img || !img.complete) {
    return;
  }
  // 旋转图标大小独立于缩放角点大小（cornerSize）
  const size = EDIT_IMAGE_ROTATE_ICON_SIZE;
  const x = left - size / 2;
  const y = top - size / 2;

  ctx.save();
  try {
    ctx.drawImage(img, x, y, size, size);
  } catch (e) {
    // ignore
  }
  ctx.restore();
}

/**
 * editImageNew：仅保留四角控件（tl/bl/br 缩放，tr 旋转），并设置红色虚线外框
 * 注意：仅对可编辑对象使用（selectable/hasControls=true）
 * @param {fabric.Object} fabricObject - Fabric 对象
 * @param {'tl'|'tr'|'bl'|'br'} [rotateCorner] - 旋转图标所在角（不传则使用全局默认）
 */
export function applyEditImageCornerControls(fabricObject, rotateCorner) {
  if (!fabricObject) {
    return;
  }

  // 1) 外框样式（红色虚线）
  try {
    fabricObject.set({
      borderColor: EDIT_IMAGE_CONTROL_COLOR,
      borderDashArray: EDIT_IMAGE_BORDER_DASH_ARRAY,
      borderScaleFactor: EDIT_IMAGE_BORDER_SCALE_FACTOR,
      cornerColor: EDIT_IMAGE_CONTROL_COLOR,
      cornerStrokeColor: EDIT_IMAGE_CONTROL_COLOR,
      cornerSize: EDIT_IMAGE_SCALE_CORNER_SIZE,
      transparentCorners: false
    });
  } catch (e) {
    // ignore
  }

  // 2) 仅保留四角（隐藏边中点 + mtr）
  if (typeof fabricObject.setControlsVisibility === 'function') {
    fabricObject.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
      mtr: false,
      tl: true,
      tr: true,
      bl: true,
      br: true
    });
  }

  // 3) 指定角替换为旋转控件（默认右上角 tr；使用内置旋转 handler，render 用 icon_move）
  if (fabricObject.controls) {
    const corner = normalizeEditImageRotateCorner(
      rotateCorner ? rotateCorner : editImageRotateCorner
    );
    const cornerXY = getEditImageCornerXY(corner);

    fabricObject.controls[corner] = new Control({
      x: cornerXY.x,
      y: cornerXY.y,
      // 旋转控件的交互区域大小（与图标大小一致）
      sizeX: EDIT_IMAGE_ROTATE_ICON_SIZE,
      sizeY: EDIT_IMAGE_ROTATE_ICON_SIZE,
      // 使用内置旋转逻辑（带吸附的版本），与 editImageNew 的 object:modified 90°吸附兼容
      actionHandler: controlsUtils.rotationWithSnapping,
      cursorStyleHandler: controlsUtils.rotationStyleHandler,
      actionName: 'rotate',
      render: renderMoveIconControl
    });
  }
}

// ==============================
// Fabric 自定义图层控件（edit 页面用）
// ==============================
const LAYER_CONTROL_ICON_SIZE = 28;
const LAYER_CONTROL_HIT_SIZE = 30;
const LAYER_CONTROL_COLOR = '#E60012';
const LAYER_CONTROL_BORDER_DASH_ARRAY = [6, 6];
const LAYER_CONTROL_BORDER_SCALE_FACTOR = 2;
const LAYER_CONTROL_SCALE_CORNER_SIZE = 6;

function getLayerControlPoint(fabricObject, corner) {
  if (!fabricObject) return new Point(0, 0);
  const width = fabricObject.width || 0;
  const height = fabricObject.height || 0;
  const scaleX = fabricObject.scaleX || 1;
  const scaleY = fabricObject.scaleY || 1;
  const paddingX =
    fabricObject._subjectControlPaddingX ||
    fabricObject._subjectControlPadding ||
    0;
  const paddingY =
    fabricObject._subjectControlPaddingY ||
    fabricObject._subjectControlPadding ||
    0;
  const padX = paddingX / scaleX;
  const padY = paddingY / scaleY;
  const left = -padX;
  const top = -padY;
  const right = width + padX;
  const bottom = height + padY;
  let localX = left;
  let localY = top;
  if (corner === 'tr') {
    localX = right;
    localY = top;
  } else if (corner === 'br') {
    localX = right;
    localY = bottom;
  } else if (corner === 'bl') {
    localX = left;
    localY = bottom;
  }
  const angle = ((fabricObject.angle || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const center = fabricObject.getCenterPoint();
  const px = (localX - width / 2) * scaleX;
  const py = (localY - height / 2) * scaleY;
  return new Point(
    px * cos - py * sin + center.x,
    px * sin + py * cos + center.y
  );
}

function createLayerControlPositionHandler(corner) {
  return function layerControlPositionHandler(dim, finalMatrix, fabricObject) {
    return getLayerControlPoint(fabricObject, corner);
  };
}

/**
 * 绘制图层自定义操作框红色虚线边框
 * @param {CanvasRenderingContext2D} ctx - Fabric 控件渲染上下文
 * @param {fabric.Object} fabricObject - 当前选中的 Fabric 图层对象
 * @returns {void}
 */
function renderLayerControlBorder(ctx, fabricObject) {
  if (!ctx || !fabricObject) return;
  const tl = getLayerControlPoint(fabricObject, 'tl');
  const tr = getLayerControlPoint(fabricObject, 'tr');
  const br = getLayerControlPoint(fabricObject, 'br');
  const bl = getLayerControlPoint(fabricObject, 'bl');
  ctx.save();
  try {
    ctx.strokeStyle = LAYER_CONTROL_COLOR;
    ctx.lineWidth = LAYER_CONTROL_BORDER_SCALE_FACTOR;
    ctx.setLineDash(LAYER_CONTROL_BORDER_DASH_ARRAY);
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y);
    ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y);
    ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
    ctx.stroke();
  } catch (e) {
    /* ignore */
  }
  ctx.restore();
}

// 缓存图标 Image 对象
let cachedDeleteIcon = null;
let cachedDeleteIconBinded = false;
let cachedCopyIcon = null;
let cachedCopyIconBinded = false;
let cachedScaleIcon = null;
let cachedScaleIconBinded = false;
let cachedRotateIcon = null;
let cachedRotateIconBinded = false;

function ensureIconImage(iconUrl, cachedRef, bindFlagRef, fabricObject) {
  if (typeof Image === 'undefined') return null;
  if (!cachedRef) {
    cachedRef = new Image();
    cachedRef.src = iconUrl;
  }
  if (!bindFlagRef) {
    bindFlagRef = true;
    cachedRef.onload = function () {
      try {
        if (fabricObject && fabricObject.canvas) {
          if (typeof fabricObject.canvas.requestRenderAll === 'function') {
            fabricObject.canvas.requestRenderAll();
          } else if (typeof fabricObject.canvas.renderAll === 'function') {
            fabricObject.canvas.renderAll();
          }
        }
      } catch (e) {
        /* ignore */
      }
    };
  }
  return cachedRef;
}

function createIconRenderer(iconUrl, getCached, setCached) {
  return function renderIconControl(
    ctx,
    left,
    top,
    styleOverride,
    fabricObject
  ) {
    let img = getCached();
    if (!img) {
      img = new Image();
      img.src = iconUrl;
      setCached(img);
      img.onload = function () {
        try {
          if (fabricObject && fabricObject.canvas) {
            if (typeof fabricObject.canvas.requestRenderAll === 'function') {
              fabricObject.canvas.requestRenderAll();
            } else if (typeof fabricObject.canvas.renderAll === 'function') {
              fabricObject.canvas.renderAll();
            }
          }
        } catch (e) {
          /* ignore */
        }
      };
    }
    if (!img.complete) return;
    const size = LAYER_CONTROL_ICON_SIZE;
    const x = left - size / 2;
    const y = top - size / 2;
    ctx.save();
    try {
      ctx.drawImage(img, x, y, size, size);
    } catch (e) {
      /* ignore */
    }
    ctx.restore();
  };
}

const renderDeleteControl = createIconRenderer(
  iconDeleteUrl,
  () => cachedDeleteIcon,
  (img) => {
    cachedDeleteIcon = img;
  }
);

const renderCopyControl = createIconRenderer(
  iconCopyUrl,
  () => cachedCopyIcon,
  (img) => {
    cachedCopyIcon = img;
  }
);

const renderScaleControl = createIconRenderer(
  iconScaleUrl,
  () => cachedScaleIcon,
  (img) => {
    cachedScaleIcon = img;
  }
);

const renderRotateControl = createIconRenderer(
  iconMoveUrl,
  () => cachedRotateIcon,
  (img) => {
    cachedRotateIcon = img;
  }
);

const LAYER_ROTATE_SNAP_RANGE = constant.SNAP_ANGLE_CORRECTION_RANGE;
const LAYER_ROTATE_SNAP_INTERVAL = 90;

function snapLayerControlAngle(angle) {
  if (typeof angle !== 'number') return angle;
  const nearest =
    Math.round(angle / LAYER_ROTATE_SNAP_INTERVAL) * LAYER_ROTATE_SNAP_INTERVAL;
  return Math.abs(angle - nearest) <= LAYER_ROTATE_SNAP_RANGE // 小于6度旋转吸附
    ? nearest
    : angle;
}

/**
 * 右下角组合 handler：等比缩放 + 旋转
 * 拖拽右下角时，以对象中心为锚点同时缩放和旋转
 * - 旋转：鼠标相对中心的角度变化
 * - 缩放：鼠标相对中心的距离变化
 * - 最大尺寸：按旋转后的实际渲染包围盒限制，不能超出画布可见区域
 * - 最小尺寸：最长物理边不得小于 15mm（通过纸张宽高和画布像素换算）
 * @param {Object} options - 缩放限制参数
 * @param {number} options.paperHeight - 纸张物理高度，单位 mm
 * @param {number} options.paperWidth - 纸张物理宽度，单位 mm
 * @param {number} options.canvansWidth - 纸张宽度对应的画布像素值
 * @param {number} options.canvansHeight - 纸张高度对应的画布像素值
 * @param {number} [options.minLongSideMm=15] - 最小长边物理尺寸（mm）
 * @param {number} [options.strokeWidth=0] - 当前图层描边宽度（mm）
 * @returns {Function} Fabric 控件 actionHandler
 */
const scalingAndRotatingHandler = (options = {}) => {
  function scaleAndRotateObject(eventData, transform, x, y) {
    const target = transform.target;
    const { ex, ey, theta } = transform;

    // 保存变换前的中心点（旋转/缩放都围绕中心进行）
    const center = target.getCenterPoint();

    // --- 等比缩放计算：鼠标相对中心的距离变化 ---
    const lastDist = Math.sqrt((ex - center.x) ** 2 + (ey - center.y) ** 2);
    const curDist = Math.sqrt((x - center.x) ** 2 + (y - center.y) ** 2);
    const ratio = curDist / Math.max(lastDist, 0.001);
    const { original } = transform;
    let newScaleX = (original.scaleX || 1) * ratio;
    let newScaleY = (original.scaleY || 1) * ratio;

    const paperWidth = Number(options.paperWidth) || 0;
    const paperHeight = Number(options.paperHeight) || 0;
    const canvansWidth = Number(options.canvansWidth) || 0;
    const canvansHeight = Number(options.canvansHeight) || 0;
    const minLongSideMm = Number(options.minLongSideMm) || 15;
    const strokeWidthMm = Number(options.strokeWidth) || 0;
    const effectiveMinLongSideMm = Math.max(1, minLongSideMm - strokeWidthMm);
    const targetWidth = target.width || 0;
    const targetHeight = target.height || 0;

    // --- 旋转计算：鼠标相对中心的角度变化 ---
    const lastAngle = Math.atan2(ey - center.y, ex - center.x);
    const curAngle = Math.atan2(y - center.y, x - center.x);
    let newAngle = (curAngle - lastAngle + theta) * (180 / Math.PI);
    if (newAngle < 0) newAngle = 360 + newAngle;
    newAngle %= 360;
    newAngle = snapLayerControlAngle(newAngle);

    /**
     * 功能：按旋转后的实际渲染包围盒约束最大缩放，保证不超出画布区域
     * @returns {void}
     */
    const clampScaleByRenderedAabb = () => {
      if (!(canvansWidth > 0 && canvansHeight > 0)) return;
      const nextWidthPx = targetWidth * newScaleX;
      const nextHeightPx = targetHeight * newScaleY;
      const angleRad = (newAngle * Math.PI) / 180;
      const absCos = Math.abs(Math.cos(angleRad));
      const absSin = Math.abs(Math.sin(angleRad));
      const nextAabbWidth = nextWidthPx * absCos + nextHeightPx * absSin;
      const nextAabbHeight = nextWidthPx * absSin + nextHeightPx * absCos;

      // 缩放阶段显示的是不带描边图：
      // 先在物理尺寸上扣掉双边描边，再换算成像素作为可显示最大尺寸
      // 例：102mm 纸张，描边 2mm -> 最大内容 98mm -> 98/102*canvasPx
      const maxAabbWidth =
        paperWidth > 0
          ? (Math.max(paperWidth - strokeWidthMm * 2, 1) / paperWidth) *
            canvansWidth
          : canvansWidth;
      const maxAabbHeight =
        paperHeight > 0
          ? (Math.max(paperHeight - strokeWidthMm * 2, 1) / paperHeight) *
            canvansHeight
          : canvansHeight;
      const clampRatio = Math.min(
        maxAabbWidth / Math.max(nextAabbWidth, 0.001),
        maxAabbHeight / Math.max(nextAabbHeight, 0.001),
        1
      );
      newScaleX *= clampRatio;
      newScaleY *= clampRatio;
    };
    clampScaleByRenderedAabb();

    const calcLongSideMmByScale = (scaleX, scaleY) => {
      if (
        !(paperWidth > 0) ||
        !(paperHeight > 0) ||
        !(canvansWidth > 0) ||
        !(canvansHeight > 0)
      ) {
        return 0;
      }
      const widthMm = ((targetWidth * scaleX) / canvansWidth) * paperWidth;
      const heightMm = ((targetHeight * scaleY) / canvansHeight) * paperHeight;
      return Math.max(widthMm, heightMm);
    };

    // 最小尺寸：缩放时展示的是不带描边图，故最小值需要扣掉当前描边宽度
    if (
      paperWidth > 0 &&
      paperHeight > 0 &&
      canvansWidth > 0 &&
      canvansHeight > 0
    ) {
      const nextLongSideMm = calcLongSideMmByScale(newScaleX, newScaleY);
      const currentLongSideMm = calcLongSideMmByScale(
        target.scaleX || 1,
        target.scaleY || 1
      );
      if (
        nextLongSideMm < effectiveMinLongSideMm &&
        nextLongSideMm < currentLongSideMm
      ) {
        const clampRatio =
          effectiveMinLongSideMm / Math.max(nextLongSideMm, 0.001);
        newScaleX *= clampRatio;
        newScaleY *= clampRatio;
      }
    }

    // 如果最小尺寸兜底后又超过最大尺寸，以渲染包围盒约束为最终结果
    clampScaleByRenderedAabb();
    // 最终兜底：防止最终约束再次把尺寸压到最小值以下
    const finalLongSideMm = calcLongSideMmByScale(newScaleX, newScaleY);
    if (finalLongSideMm > 0 && finalLongSideMm < effectiveMinLongSideMm) {
      const finalMinRatio =
        effectiveMinLongSideMm / Math.max(finalLongSideMm, 0.001);
      newScaleX *= finalMinRatio;
      newScaleY *= finalMinRatio;
    }

    // 应用缩放和旋转
    target.angle = newAngle;
    target.scaleX = newScaleX;
    target.scaleY = newScaleY;
    target.setPositionByOrigin(center, 'center', 'center');

    return true;
  }

  return controlsUtils.wrapWithFireEvent('modifying', scaleAndRotateObject);
};

/**
 * edit 页面：自定义图层控件
 * - 左上角(tl)：删除
 * - 右上角(tr)：复制
 * - 右下角(br)：等比缩放 + 旋转（单指拖动同时缩放和旋转）
 * - 隐藏其他默认控件（边中点、旋转）
 * @param {fabric.Object} fabricObject - Fabric 对象
 */
export function applyLayerCustomControls(fabricObject, callbacks) {
  if (!fabricObject) return;

  // 1) 外框样式（红色虚线）
  try {
    fabricObject.set({
      borderColor: LAYER_CONTROL_COLOR,
      borderDashArray: LAYER_CONTROL_BORDER_DASH_ARRAY,
      borderScaleFactor: LAYER_CONTROL_BORDER_SCALE_FACTOR,
      hasBorders: false,
      cornerColor: LAYER_CONTROL_COLOR,
      cornerStrokeColor: LAYER_CONTROL_COLOR,
      cornerSize: LAYER_CONTROL_SCALE_CORNER_SIZE,
      transparentCorners: false
    });
  } catch (e) {
    /* ignore */
  }

  // 2) 隐藏边中点 + 旋转控件
  if (typeof fabricObject.setControlsVisibility === 'function') {
    fabricObject.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
      mtr: false,
      tl: true,
      tr: true,
      bl: false,
      br: true
    });
  }

  // 3) 替换控件
  if (fabricObject.controls) {
    // 左上角 → 删除
    fabricObject.controls.tl = new Control({
      x: -0.5,
      y: -0.5,
      sizeX: LAYER_CONTROL_HIT_SIZE,
      sizeY: LAYER_CONTROL_HIT_SIZE,
      touchSizeX: LAYER_CONTROL_HIT_SIZE,
      touchSizeY: LAYER_CONTROL_HIT_SIZE,
      cursorStyle: 'pointer',
      positionHandler: createLayerControlPositionHandler('tl'),
      mouseDownHandler: (a, b) => {
        console.log('删除图层事件对象', b);
        const id = b.target.layerId;
        callbacks.onLayerDelete(id);
      },
      render: function renderDeleteControlWithBorder(
        ctx,
        left,
        top,
        styleOverride,
        fabricObject
      ) {
        renderLayerControlBorder(ctx, fabricObject);
        renderDeleteControl(ctx, left, top, styleOverride, fabricObject);
      }
    });

    // 右上角 → 复制
    fabricObject.controls.tr = new Control({
      x: 0.5,
      y: -0.5,
      sizeX: LAYER_CONTROL_HIT_SIZE,
      sizeY: LAYER_CONTROL_HIT_SIZE,
      touchSizeX: LAYER_CONTROL_HIT_SIZE,
      touchSizeY: LAYER_CONTROL_HIT_SIZE,
      cursorStyle: 'pointer',
      positionHandler: createLayerControlPositionHandler('tr'),
      mouseDownHandler: (a, b) => {
        console.log('复制图层事件对象', b);
        const target = b && b.target;
        const id = target && target.layerId;
        callbacks.onLayerCopy(id, target);
      },
      render: renderCopyControl
    });

    // 右下角 → 等比缩放 + 旋转（组合 handler）
    fabricObject.controls.br = new Control({
      x: 0.5,
      y: 0.5,
      sizeX: LAYER_CONTROL_HIT_SIZE,
      sizeY: LAYER_CONTROL_HIT_SIZE,
      touchSizeX: LAYER_CONTROL_HIT_SIZE,
      touchSizeY: LAYER_CONTROL_HIT_SIZE,
      cursorStyle: 'se-resize',
      actionHandler: scalingAndRotatingHandler({
        paperHeight: callbacks.paperHeight,
        paperWidth: callbacks.paperWidth,
        canvansWidth: callbacks.canvansWidth,
        canvansHeight: callbacks.canvansHeight,
        minLongSideMm: callbacks.minLongSideMm,
        strokeWidth: Number(fabricObject.strokeWidth) || 0
      }),
      actionName: 'scale',
      positionHandler: createLayerControlPositionHandler('br'),
      render: renderScaleControl
    });
  }
}

/**
 * 生成图层唯一ID
 * @returns {string} 格式为 layer_${timestamp}_${random} 的唯一ID
 */
export function generateLayerId() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `layer_${timestamp}_${random}`;
}

/**
 * 创建画布信息
 * @param {Object} options - 画布配置选项
 * @param {number} options.mmWidth - 物理宽度（单位：mm）
 * @param {number} options.mmHeight - 物理高度（单位：mm）
 * @param {number} [options.dpi=300] - 分辨率，默认300
 * @param {number} [options.displayWidth] - 显示像素宽度（单位：px），用于预览画布
 * @param {number} [options.displayHeight] - 显示像素高度（单位：px），用于预览画布
 * @returns {Object} 完整的 canvas 对象
 */
export function initCanvasInfo(options) {
  if (!options) {
    throw new Error('initCanvasInfo: options 参数不能为空');
  }

  const { mmWidth, mmHeight, dpi = 300, displayWidth, displayHeight } = options;

  if (typeof mmWidth !== 'number' || mmWidth <= 0) {
    throw new Error('initCanvasInfo: mmWidth 必须是大于0的数字');
  }

  if (typeof mmHeight !== 'number' || mmHeight <= 0) {
    throw new Error('initCanvasInfo: mmHeight 必须是大于0的数字');
  }

  // 计算生产像素尺寸：mmWidth * dpi / 25.4
  const productionWidth = Math.round((mmWidth * dpi) / 25.4);
  const productionHeight = Math.round((mmHeight * dpi) / 25.4);

  // 如果没有指定显示尺寸，使用生产尺寸
  const finalDisplayWidth =
    typeof displayWidth === 'number' && displayWidth > 0
      ? displayWidth
      : productionWidth;
  const finalDisplayHeight =
    typeof displayHeight === 'number' && displayHeight > 0
      ? displayHeight
      : productionHeight;

  return {
    mmWidth,
    mmHeight,
    dpi,
    productionWidth,
    productionHeight,
    displayWidth: finalDisplayWidth,
    displayHeight: finalDisplayHeight
  };
}

/**
 * 创建图层画布信息（Fabric.js 格式）
 * @param {Object} options - 图层画布信息配置选项
 * @param {number} [options.left=0] - 图层在画布中的 left 坐标（单位：px），对齐 Fabric.js
 * @param {number} [options.top=0] - 图层在画布中的 top 坐标（单位：px），对齐 Fabric.js
 * @param {number} options.width - 图层原始宽度（单位：px），与 Fabric.js 对齐
 * @param {number} options.height - 图层原始高度（单位：px），与 Fabric.js 对齐
 * @param {number} [options.angle=0] - 图层在画布中的旋转角度（单位：度），与 Fabric.js 对齐
 * @param {number} [options.scaleX] - X 轴缩放比例，对齐 Fabric.js
 * @param {number} [options.scaleY] - Y 轴缩放比例，对齐 Fabric.js
 * @param {string} [options.originX='left'] - X 轴原点，对齐 Fabric.js
 * @param {string} [options.originY='top'] - Y 轴原点，对齐 Fabric.js
 * @returns {Object} 完整的 layerInfo 对象
 */
export function createLayerInfo(options) {
  if (!options) {
    throw new Error('createLayerInfo: options 参数不能为空');
  }

  const {
    left = 0,
    top = 0,
    width,
    height,
    angle = 0,
    scaleX,
    scaleY,
    originX = 'left',
    originY = 'top'
  } = options;

  if (typeof width !== 'number' || width <= 0) {
    throw new Error('createLayerInfo: width 必须是大于0的数字');
  }

  if (typeof height !== 'number' || height <= 0) {
    throw new Error('createLayerInfo: height 必须是大于0的数字');
  }

  // 如果没有提供 scaleX/scaleY，默认为 1
  const finalScaleX = typeof scaleX === 'number' && scaleX > 0 ? scaleX : 1;
  const finalScaleY = typeof scaleY === 'number' && scaleY > 0 ? scaleY : 1;

  return {
    left,
    top,
    width,
    height,
    scaleX: finalScaleX,
    scaleY: finalScaleY,
    angle,
    originX,
    originY
  };
}

/**
 * 批量设置字段（仅复制已定义字段）
 * @param {Object} target - 目标对象
 * @param {Object} source - 源对象
 * @param {Array} fields - 需要复制的字段名列表
 */
function assignDefinedFields(target, source, fields) {
  if (!target || !source || !Array.isArray(fields)) {
    return;
  }
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (source[field] !== undefined) {
      target[field] = source[field];
    }
  }
}

/**
 * 创建图层对象
 * @param {Object} options - 图层配置选项
 * @param {string} options.type - 图层类型：userImage、skuTemplate、skuMaterial、background
 * @param {string} options.imageUrl - 图片地址
 * @param {Object} options.layerInfo - 图层在画布中的位置、大小和变换信息
 * @param {boolean} [options.canEditImage] - 是否可以进行图片编辑操作，根据类型自动设置
 * @param {boolean} [options.canAdjustLayer] - 是否可以调整图层上下层级，根据类型自动设置
 * @param {string} [options.id] - 图层唯一标识，如果不提供则自动生成
 * @param {number} [options.zIndex] - 图层层级，如果不提供则自动计算
 * @param {Object} [options.typeSpecificFields] - 类型特有字段
 * @returns {Object} 完整的 layer 对象
 */
export function createLayer(options) {
  if (!options) {
    throw new Error('createLayer: options 参数不能为空');
  }

  const {
    type,
    imageUrl,
    layerInfo,
    canEditImage,
    canAdjustLayer,
    id,
    zIndex,
    ...typeSpecificFields
  } = options;

  // 验证必需字段
  if (!type) {
    throw new Error('createLayer: type 参数不能为空');
  }

  const validTypes = [
    constant.layer_userImage,
    constant.layer_template,
    constant.layer_skuMaterial,
    constant.layer_background,
    constant.layer_transparentGrid
  ];
  if (validTypes.indexOf(type) === -1) {
    throw new Error(
      `createLayer: type 必须是以下值之一：${validTypes.join(', ')}`
    );
  }

  if (!imageUrl) {
    throw new Error('createLayer: imageUrl 参数不能为空');
  }

  // userImage 类型需要验证 originImageUrl 和 originImageFileName
  if (type === constant.layer_userImage) {
    if (!typeSpecificFields.originImageUrl) {
      throw new Error(
        'createLayer: type=userImage 时，originImageUrl 参数不能为空'
      );
    }
    // if (!typeSpecificFields.originImageFileName) {
    //   throw new Error(
    //     'createLayer: type=userImage 时，originImageFileName 参数不能为空'
    //   );
    // }
    // userImage 的 zIndex 必须 >= 0
    // if (typeof zIndex === 'number' && zIndex < 0) {
    //   throw new Error('createLayer: type=userImage 时，zIndex 必须 >= 0');
    // }
  }

  // background 类型的 zIndex 必须 <= -100
  if (type === constant.layer_background) {
    if (typeof zIndex === 'number' && zIndex > backgroundMaxZIndex) {
      throw new Error(
        `createLayer: type=background 时，zIndex 必须 <= ${backgroundMaxZIndex}`
      );
    }
  }

  // transparentGrid 类型的 zIndex 必须 <= backgroundMaxZIndex（确保在所有普通图层之下）
  if (type === constant.layer_transparentGrid) {
    if (typeof zIndex === 'number' && zIndex > backgroundMaxZIndex) {
      throw new Error(
        `createLayer: type=transparentGrid 时，zIndex 必须 <= ${backgroundMaxZIndex}`
      );
    }
  }

  // if (!layerInfo) {
  //   throw new Error('createLayer: layerInfo 参数不能为空');
  // }

  // 根据类型设置默认值
  let defaultCanEditImage = true;
  let defaultCanAdjustLayer = true;

  if (type === constant.layer_background) {
    defaultCanAdjustLayer = false;
  } else if (type === constant.layer_template) {
    // skuTemplate 的 canEditImage 由模板配置决定，这里设为 false 作为默认值
    defaultCanEditImage = false;
  }

  // 根据类型设置默认 zIndex
  let defaultZIndex = 0;
  if (type === constant.layer_background) {
    defaultZIndex = backgroundMaxZIndex; // background 默认 zIndex 为 -100
  } else if (type === constant.layer_transparentGrid) {
    defaultZIndex = transparentGridZIndex;
  } else if (type === constant.layer_userImage) {
    defaultZIndex = 0; // userImage 默认 zIndex 为 0（必须 >= 0）
  }

  // 创建基础图层对象
  const layer = {
    id: id || generateLayerId(),
    type,
    zIndex: typeof zIndex === 'number' ? zIndex : defaultZIndex,
    imageUrl,
    layerInfo,
    canEditImage:
      typeof canEditImage === 'boolean' ? canEditImage : defaultCanEditImage,
    canAdjustLayer:
      typeof canAdjustLayer === 'boolean'
        ? canAdjustLayer
        : defaultCanAdjustLayer
  };

  // 添加类型特有字段
  if (type === constant.layer_userImage) {
    // 用户原图特有字段
    layer.originImageUrl = typeSpecificFields.originImageUrl;
    layer.originImageFileName = typeSpecificFields.originImageFileName;
    // layer.cutoutState = typeSpecificFields.cutoutState || 'none';
    // layer.cutoutStates = typeSpecificFields.cutoutStates || {
    //   currentStatus: 'none',
    //   fullBody: null,
    //   head: null,
    //   manual: null
    // };
    // layer.operationHistory = typeSpecificFields.operationHistory || [];
    layer.imageStatus = typeSpecificFields.imageStatus || {
      uploadRes: null,
      afterClipUploadRes: null,
      beforeClipRetouchRes: null,
      retouchRes: null,
      shapeClipRes: null,
      customOutlineClipOriginImageRes: null
    };
    // 抠图相关字段
    layer.retouchAction = typeSpecificFields.retouchAction || '';
    layer.retouchType = typeSpecificFields.retouchType || '';
    layer.excuteRetouch = typeSpecificFields.excuteRetouch || false;
    // 白墨相关字段
    layer.whiteInkOriginImageSrc = typeSpecificFields.whiteInkOriginImageSrc;
    layer.whiteAction = typeSpecificFields.whiteAction || '';
    // 白墨收缩（默认开启）
    layer.whiteShrink =
      typeSpecificFields.whiteShrink !== undefined
        ? typeSpecificFields.whiteShrink
        : true;
    // 描边宽度字段
    layer.strokeWidth = typeSpecificFields.strokeWidth;
    if (typeSpecificFields.strokeDisplayInfo) {
      layer.strokeDisplayInfo = typeSpecificFields.strokeDisplayInfo;
    }
    // 反面图(不是正反不同图)
    if (typeSpecificFields.isReverseLayerSku) {
      layer.reversePrimerLayerImageUrl =
        typeSpecificFields.reversePrimerLayerImageUrl || '';
      layer.reversePrimerLayerAction =
        typeSpecificFields.reversePrimerLayerAction ||
        constant.ReversePrimerLayerActionMap.AUTO;
      layer.scopeUv = typeSpecificFields.scopeUv || '';
      layer.scopeUvAction =
        typeSpecificFields.scopeUvAction || constant.ScopeUvActionMap.EMPTY;
    }
    // 原图信息字段（可选，只有当原图发生尺寸缩放或裁剪时才有此字段）
    assignDefinedFields(layer, typeSpecificFields, [
      'originImageInfo',
      'whiteInk'
    ]);
  } else if (type === constant.layer_template) {
    // sku 模板特有字段
    assignDefinedFields(layer, typeSpecificFields, [
      'whiteInk',
      'isClearTemplate',
      'maskUrl',
      'backUrl',
      'spotUv',
      'frostUrl'
    ]);
  } else if (type === constant.layer_skuMaterial) {
    // sku 素材特有字段
    if (typeSpecificFields.materialId) {
      layer.materialId = typeSpecificFields.materialId;
    }
    layer.source = typeSpecificFields.source || 'system';
    assignDefinedFields(layer, typeSpecificFields, [
      'originImageUrl',
      'originImageFileName',
      'imageStatus',
      'originImageInfo',
      'strokeWidth',
      'strokeColor',
      'initOutLineInfo',
      'strokeDisplayInfo',
      'couldRetouch'
    ]);
  }

  // 非阻塞校验：layerInfo.width/height 是否与 imageUrl 对应图片真实宽高一致
  validateLayerInfoImageSizeNonBlocking(layer, 'createLayer');
  return layer;
}

/**
 * 获取图层数组中的最大 zIndex
 * @param {Array} layers - 图层数组
 * @returns {number} 最大 zIndex，如果没有图层则返回 0
 */
export function getMaxZIndex(layers) {
  if (!layers || !Array.isArray(layers) || layers.length === 0) {
    return 0;
  }

  let maxZIndex = 0;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && typeof layer.zIndex === 'number' && layer.zIndex > maxZIndex) {
      maxZIndex = layer.zIndex;
    }
  }

  return maxZIndex;
}

/**
 * 获取图层数组中的最小 zIndex
 * @param {Array} layers - 图层数组
 * @returns {number} 最小 zIndex，如果没有图层则返回 0
 */
export function getMinZIndex(layers) {
  if (!layers || !Array.isArray(layers) || layers.length === 0) {
    return 0;
  }

  let minZIndex = Infinity;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && typeof layer.zIndex === 'number' && layer.zIndex < minZIndex) {
      minZIndex = layer.zIndex;
    }
  }

  return minZIndex === Infinity ? 0 : minZIndex;
}

/**
 * 按 zIndex 排序图层数组
 * @param {Array} layers - 图层数组
 * @returns {Array} 排序后的图层数组（不修改原数组）
 */
export function sortLayersByZIndex(layers) {
  if (!layers || !Array.isArray(layers)) {
    return [];
  }

  // 创建副本并排序
  return layers.slice().sort((a, b) => {
    const zIndexA = a && typeof a.zIndex === 'number' ? a.zIndex : 0;
    const zIndexB = b && typeof b.zIndex === 'number' ? b.zIndex : 0;
    return zIndexA - zIndexB;
  });
}

/**
 * 添加图层到 layerData
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {Object} layer - 要添加的图层对象
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function addLayer(layerData, layer) {
  if (!layerData) {
    throw new Error('addLayer: layerData 参数不能为空');
  }

  if (!layer) {
    throw new Error('addLayer: layer 参数不能为空');
  }

  // 创建 layerData 的副本
  const newLayerData = {
    ...layerData,
    layers: layerData.layers ? [...layerData.layers] : []
  };

  // 如果图层没有 zIndex，自动计算
  if (typeof layer.zIndex !== 'number') {
    const maxZIndex = getMaxZIndex(newLayerData.layers);
    layer.zIndex = maxZIndex + 1;
  }

  // 添加图层
  newLayerData.layers.push(layer);

  // 按 zIndex 排序
  newLayerData.layers = sortLayersByZIndex(newLayerData.layers);

  // 非阻塞校验：layerInfo.width/height 是否与 imageUrl 对应图片真实宽高一致
  validateLayerInfoImageSizeNonBlocking(layer, 'addLayer');
  return newLayerData;
}

/**
 * 获取指定ID的图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {string} layerId - 图层ID
 * @returns {Object|null} 找到的图层对象，如果不存在则返回 null
 */
export function getLayer(layerData, layerId) {
  if (!layerData) {
    throw new Error('getLayer: layerData 参数不能为空');
  }

  if (!layerId) {
    throw new Error('getLayer: layerId 参数不能为空');
  }

  if (!layerData.layers || !Array.isArray(layerData.layers)) {
    return null;
  }

  for (let i = 0; i < layerData.layers.length; i++) {
    const layer = layerData.layers[i];
    if (layer && layer.id === layerId) {
      return layer;
    }
  }

  return null;
}

/**
 * 更新图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {string} layerId - 要更新的图层ID
 * @param {Object} updates - 要更新的字段对象
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function updateLayer(layerData, layerId, updates) {
  if (!layerData) {
    throw new Error('updateLayer: layerData 参数不能为空');
  }

  if (!layerId) {
    throw new Error('updateLayer: layerId 参数不能为空');
  }

  if (!updates || typeof updates !== 'object') {
    throw new Error('updateLayer: updates 参数必须是一个对象');
  }

  // 创建 layerData 的副本
  const newLayerData = {
    ...layerData,
    layers: layerData.layers ? [...layerData.layers] : []
  };

  // 查找并更新图层
  let found = false;
  for (let i = 0; i < newLayerData.layers.length; i++) {
    const layer = newLayerData.layers[i];
    if (layer && layer.id === layerId) {
      newLayerData.layers[i] = {
        ...layer,
        ...updates
      };
      // 非阻塞校验：layerInfo.width/height 是否与 imageUrl 对应图片真实宽高一致
      validateLayerInfoImageSizeNonBlocking(
        newLayerData.layers[i],
        'updateLayer'
      );
      found = true;
      break;
    }
  }

  if (!found) {
    throw new Error(`updateLayer: 未找到ID为 ${layerId} 的图层`);
  }

  // 如果更新了 zIndex，需要重新排序
  if (typeof updates.zIndex === 'number') {
    newLayerData.layers = sortLayersByZIndex(newLayerData.layers);
  }

  return newLayerData;
}

/**
 * 删除图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {string} layerId - 要删除的图层ID
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function removeLayer(layerData, layerId) {
  if (!layerData) {
    throw new Error('removeLayer: layerData 参数不能为空');
  }

  if (!layerId) {
    throw new Error('removeLayer: layerId 参数不能为空');
  }

  // 创建 layerData 的副本
  const newLayerData = {
    ...layerData,
    layers: layerData.layers ? [...layerData.layers] : []
  };

  // 查找并删除图层
  let found = false;
  for (let i = 0; i < newLayerData.layers.length; i++) {
    const layer = newLayerData.layers[i];
    if (layer && layer.id === layerId) {
      newLayerData.layers.splice(i, 1);
      found = true;
      break;
    }
  }

  if (!found) {
    throw new Error(`removeLayer: 未找到ID为 ${layerId} 的图层`);
  }

  return newLayerData;
}

/**
 * 初始化 layerData 数据结构
 * @param {Object} options - 初始化配置选项
 * @param {Object} options.canvas - 画布信息对象，如果提供则使用，否则需要后续设置
 * @param {Array} [options.layers=[]] - 初始图层数组
 * @param {Array} [options.reverseLayer=[]] - 反面图层数组
 * @param {number} [options.strokeWidth] - 当前画布描边宽度，0 表示不描边，未传则默认 0
 * @param {string} [options.reversePrimerLayerImageUrl] - 逆向层图片 URL，默认 ''
 * @param {string} [options.reversePrimerLayerAction] - 逆向层最近一次工具动作（见 ReversePrimerLayerActionMap），默认 ''
 * @param {string} [options.scopeUv] - 局部 UV 图片 URL，默认 ''
 * @param {string} [options.scopeUvAction] - 局部 UV 最近一次工具动作（见 ScopeUvActionMap），默认 ''
 * @returns {Object} 完整的 layerData 对象
 */
export function initLayerData(options) {
  const opts = options || {};

  const layerData = {
    canvas: opts.canvas || null,
    layers: Array.isArray(opts.layers) ? opts.layers : [],
    reverseLayer: Array.isArray(opts.reverseLayer) ? opts.reverseLayer : [],
    reverseLayerIsMirror:
      opts.reverseLayerIsMirror === undefined
        ? true
        : !!opts.reverseLayerIsMirror,
    reverseOutlineImageSrc: opts.reverseOutlineImageSrc || '',
    reverseOutlineOriginImageSrc: opts.reverseOutlineOriginImageSrc || '',
    reverseOutlineOriginImageFileName:
      opts.reverseOutlineOriginImageFileName || '',
    previewCompositeImage: opts.previewCompositeImage || null,
    outlineImage: opts.outlineImage || null,
    accessoryAndOutlineImage: opts.accessoryAndOutlineImage || null,
    strokeWidth: opts.strokeWidth != null ? opts.strokeWidth : 0,
    reversePrimerLayerImageUrl: opts.reversePrimerLayerImageUrl || '',
    reversePrimerLayerAction: opts.reversePrimerLayerAction || '',
    scopeUv: opts.scopeUv || '',
    scopeUvAction: opts.scopeUvAction || ''
  };

  // 如果提供了图层，确保它们按 zIndex 排序
  if (layerData.layers.length > 0) {
    layerData.layers = sortLayersByZIndex(layerData.layers);
  }
  if (layerData.reverseLayer.length > 0) {
    layerData.reverseLayer = sortLayersByZIndex(layerData.reverseLayer);
  }

  return layerData;
}

/**
 * 计算图片在画布中的位置和尺寸信息
 * @param {Object} options - 计算选项
 * @param {number} options.imageWidth - 图片原始宽度（单位：px）
 * @param {number} options.imageHeight - 图片原始高度（单位：px）
 * @param {number} options.canvasWidth - 画布宽度（单位：px）
 * @param {number} options.canvasHeight - 画布高度（单位：px）
 * @param {string} [options.type] - 布局类型：ImageLayoutTypeMap.fit / fill
 * @returns {Object} drawCanvasInfo 对象
 */
export function calcImageInCanvasPosition({
  imageWidth,
  imageHeight,
  canvasWidth,
  canvasHeight,
  type = constant.ImageLayoutTypeMap.fit
}) {
  if (typeof imageWidth !== 'number' || imageWidth <= 0) {
    throw new Error('calculateDrawCanvasInfo: imageWidth 必须是大于0的数字');
  }

  if (typeof imageHeight !== 'number' || imageHeight <= 0) {
    throw new Error('calculateDrawCanvasInfo: imageHeight 必须是大于0的数字');
  }

  if (typeof canvasWidth !== 'number' || canvasWidth <= 0) {
    throw new Error('calculateDrawCanvasInfo: canvasWidth 必须是大于0的数字');
  }

  if (typeof canvasHeight !== 'number' || canvasHeight <= 0) {
    throw new Error('calculateDrawCanvasInfo: canvasHeight 必须是大于0的数字');
  }

  const imageRatio = imageWidth / imageHeight;
  const canvasRatio = canvasWidth / canvasHeight;
  let inCanvasImageWidth;
  let inCanvasImageHeight;
  let baseType;

  if (type === constant.ImageLayoutTypeMap.fit) {
    // 适应模式：短边撑满，长边居中
    if (imageRatio < canvasRatio) {
      // 图片更高，高度撑满
      inCanvasImageHeight = canvasHeight;
      inCanvasImageWidth = inCanvasImageHeight * imageRatio;
      baseType = 'height';
    } else {
      // 图片更宽，宽度撑满
      inCanvasImageWidth = canvasWidth;
      inCanvasImageHeight = inCanvasImageWidth / imageRatio;
      baseType = 'width';
    }
  } else if (type === constant.ImageLayoutTypeMap.fill) {
    // 填充模式：长边撑满，短边居中
    if (imageRatio > canvasRatio) {
      // 图片更宽，高度撑满（长边）
      inCanvasImageHeight = canvasHeight;
      inCanvasImageWidth = inCanvasImageHeight * imageRatio;
      baseType = 'height';
    } else {
      // 图片更高，宽度撑满（长边）
      inCanvasImageWidth = canvasWidth;
      inCanvasImageHeight = inCanvasImageWidth / imageRatio;
      baseType = 'width';
    }
  } else {
    throw new Error(
      'calculateDrawCanvasInfo: type 必须是 fit 或 fill（见 ImageLayoutTypeMap）'
    );
  }

  // 居中显示
  const x = (canvasWidth - inCanvasImageWidth) / 2;
  const y = (canvasHeight - inCanvasImageHeight) / 2;

  return {
    originImageWidth: imageWidth,
    originImageHeight: imageHeight,
    inCanvasImageWidth: inCanvasImageWidth,
    inCanvasImageHeight: inCanvasImageHeight,
    initImageInCanvasWidth: inCanvasImageWidth,
    prevTouchWidth: inCanvasImageWidth,
    prevTouchHeight: inCanvasImageHeight,
    currStatus: {
      x: x,
      y: y,
      scale: 1
    },
    prevStatus: {
      x: x,
      y: y,
      scale: 1
    },
    baseType: baseType
  };
}

/**
 * 验证图片 URL
 * @param {any} imageUrl - 要验证的值
 * @param {string} paramName - 参数名称，用于错误信息
 * @throws {Error} 如果验证失败
 */
export function validateImageUrl(imageUrl, paramName = 'imageUrl') {
  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new Error(`${paramName} 必须是字符串且不为空`);
  }
}

/**
 * 验证画布信息
 * @param {any} canvasInfo - 要验证的值
 * @param {string} paramName - 参数名称，用于错误信息
 * @throws {Error} 如果验证失败
 */
export function validateCanvasInfo(canvasInfo, paramName = 'canvasInfo') {
  if (!canvasInfo || typeof canvasInfo !== 'object') {
    throw new Error(`${paramName} 必须是对象`);
  }
  if (
    typeof canvasInfo.displayWidth !== 'number' ||
    canvasInfo.displayWidth <= 0
  ) {
    throw new Error(`${paramName}.displayWidth 必须是大于0的数字`);
  }
  if (
    typeof canvasInfo.displayHeight !== 'number' ||
    canvasInfo.displayHeight <= 0
  ) {
    throw new Error(`${paramName}.displayHeight 必须是大于0的数字`);
  }
}

/**
 * 验证布局类型
 * @param {any} type - 要验证的值
 * @param {string} paramName - 参数名称，用于错误信息
 * @throws {Error} 如果验证失败
 */
export function validateLayoutType(type, paramName = 'type') {
  var L = constant.ImageLayoutTypeMap;
  if (type !== L.fit && type !== L.fill && type !== L.fitRemoveTransparent) {
    throw new Error(
      paramName + ' 必须是 ImageLayoutTypeMap.fit、fill 或 fitRemoveTransparent'
    );
  }
}

/**
 * 获取 skuTemplate 类型的图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @returns {Object|null} 找到的第一个 skuTemplate layer，如果不存在则返回 null
 */
export function getSkuTemplateLayer(layerData) {
  if (!layerData) {
    return null;
  }

  if (!layerData.layers || !Array.isArray(layerData.layers)) {
    return null;
  }

  for (let i = 0; i < layerData.layers.length; i++) {
    const layer = layerData.layers[i];
    if (layer && layer.type === constant.layer_template) {
      return layer;
    }
  }

  return null;
}

/**
 * 处理 skuTemplate maskUrl，生成处理后的遮罩层图片 URL
 * @param {Object} options - 参数对象
 * @param {Object} options.layerData - 完整的 layerData 对象
 * @param {string} options.hexColor - 替换黑色像素的颜色，默认 '#fdeeed'
 * @param {number} options.maxWidth - 图片的最大宽度
 * @param {number} options.maxHeight - 图片的最大高度
 * @param {boolean} [options.cache=true] - 是否启用缓存，默认 true
 * @returns {Promise<string>} 返回处理后的图片 URL，如果不存在 maskUrl 则返回空字符串
 */
export async function processSkuTemplateMaskUrl(options) {
  const { layerData, hexColor = '#fdeeed', maxWidth, maxHeight, cache = true } =
    options || {};

  if (!layerData || !layerData.layers) {
    return '';
  }

  try {
    // 获取 skuTemplate layer
    const skuTemplateLayer = getSkuTemplateLayer(layerData);
    if (!skuTemplateLayer) {
      return '';
    }

    // 获取 maskUrl
    const maskUrl =
      skuTemplateLayer &&
      typeof skuTemplateLayer.maskUrl === 'string' &&
      skuTemplateLayer.maskUrl
        ? skuTemplateLayer.maskUrl
        : '';

    if (!maskUrl) {
      return '';
    }

    // 调用 fillBlackPixelsWithHexColorByComposite 处理 maskUrl
    const processedMaskUrl = await canvasUtil.fillBlackPixelsWithHexColorByComposite(
      {
        imageSrc: maskUrl,
        hexColor: hexColor,
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        cache: cache
      }
    );
    // window._debugLog({
    //   title: 'processSkuTemplateMaskUrl',
    //   maskUrl: maskUrl
    // });

    return processedMaskUrl;
  } catch (error) {
    console.error('processSkuTemplateMaskUrl: 处理失败', error);
    return '';
  }
}

/**
 * 获取第一个 type=userImage 的 layer 在 layers 数组中的 index
 * @param {Object} layerData - 完整的 layerData 对象
 * @returns {number} layer index，如果不存在则返回 -1
 */
export function getActiveUserImageLayerIndex(layerData) {
  if (!layerData) {
    slog.error('getActiveUserImageLayerIndex: layerData 参数不能为空');
    throw new Error('getActiveUserImageLayerIndex: layerData 参数不能为空');
  }

  if (!layerData.layers || !Array.isArray(layerData.layers)) {
    slog.error('getActiveUserImageLayerIndex: layerData.layers 参数不能为空');
    throw new Error(
      'getActiveUserImageLayerIndex: layerData.layers 参数不能为空'
    );
  }

  for (let i = 0; i < layerData.layers.length; i++) {
    const layer = layerData.layers[i];
    if (layer && layer.type === constant.layer_userImage) {
      return i;
    }
  }

  return -1;
}

/**
 * 获取轮廓图路径
 * 找到第一个 type=userImage 的 layer，返回其 imageUrl
 * @param {Array} layers - layer 数组
 * @returns {string} 轮廓图路径（imageUrl），如果不存在则返回 null
 */

/**
 * 判断 skuTemplate layer 的层级模式（置顶或置底）
 * @param {Array} layers - 图层数组
 * @param {Object} templateLayer - skuTemplate layer 对象
 * @returns {string|null} 'top'（置顶）、'bottom'（置底）或 null（无法判断）
 */
export function getSkuTemplateLayerMode(layers, templateLayer) {
  if (!layers || !Array.isArray(layers) || layers.length === 0) {
    return null;
  }

  if (!templateLayer || typeof templateLayer.zIndex !== 'number') {
    return null;
  }

  const maxZIndex = getMaxZIndex(layers);
  const minZIndex = getMinZIndex(layers);

  // 如果模板的 zIndex 等于最大 zIndex，则为置顶
  if (templateLayer.zIndex === maxZIndex) {
    return 'top';
  }

  // 如果模板的 zIndex 等于最小 zIndex，则为置底
  if (templateLayer.zIndex === minZIndex) {
    return 'bottom';
  }

  // 如果既不是最大也不是最小，返回 null
  return null;
}

/**
 * 加载图片
 * @param {string} imageUrl - 图片地址
 * @returns {Promise<HTMLImageElement>} 返回加载完成的 Image 对象
 */
export function loadImage(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new Error('loadImage: imageUrl 参数不能为空且必须是字符串');
  }

  // 先从缓存中查找
  const cachedImage = imageCache.get(imageUrl);
  if (cachedImage) {
    return Promise.resolve(cachedImage);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    // 如果是 http/https 协议的图片，设置跨域属性
    if (/^http/.test(imageUrl)) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      if (img.naturalWidth) {
        img.width = img.naturalWidth;
        img.height = img.naturalHeight;
      }
      // 将加载成功的图片存入缓存
      imageCache.set(imageUrl, img);
      resolve(img);
    };

    img.onerror = (err) => {
      reject(new Error(`图片加载失败: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
}

/**
 * 验证绘制 canvas 的通用参数
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {string} methodName - 方法名称，用于错误提示
 * @param {Object} [options.layerInfo] - layerInfo 对象（可选）
 */
function validateDrawCanvasOptions(options, methodName) {
  if (!options || typeof options !== 'object') {
    throw new Error(`${methodName}: options 参数必须是一个对象`);
  }

  const { ctx, canvasWidth, canvasHeight, layerInfo } = options;

  if (!ctx || typeof ctx.drawImage !== 'function') {
    throw new Error(
      `${methodName}: ctx 参数必须是一个有效的 CanvasRenderingContext2D 对象`
    );
  }

  if (typeof canvasWidth !== 'number' || canvasWidth <= 0) {
    throw new Error(`${methodName}: canvasWidth 必须是大于0的数字`);
  }

  if (typeof canvasHeight !== 'number' || canvasHeight <= 0) {
    throw new Error(`${methodName}: canvasHeight 必须是大于0的数字`);
  }

  // 如果提供了 layerInfo，验证其字段
  if (layerInfo !== undefined) {
    if (!layerInfo || typeof layerInfo !== 'object') {
      throw new Error(`${methodName}: layerInfo 必须是一个对象`);
    }

    // 验证必要字段是否存在
    if (typeof layerInfo.left !== 'number') {
      throw new Error(`${methodName}: layerInfo.left 字段必须存在且为数字`);
    }

    if (typeof layerInfo.top !== 'number') {
      throw new Error(`${methodName}: layerInfo.top 字段必须存在且为数字`);
    }

    if (typeof layerInfo.width !== 'number' || layerInfo.width <= 0) {
      throw new Error(
        `${methodName}: layerInfo.width 字段必须存在且为大于0的数字`
      );
    }

    if (typeof layerInfo.height !== 'number' || layerInfo.height <= 0) {
      throw new Error(
        `${methodName}: layerInfo.height 字段必须存在且为大于0的数字`
      );
    }
  }
}

/**
 * 在 2D canvas 上绘制“透明网格底图”（使用 createPattern 平铺填满）
 * - 仅用于编辑 UI 展示，不参与合成导出
 * - 忽略 layerInfo，直接按 canvasWidth/canvasHeight 填满
 *
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {string} options.imageUrl - 透明网格图片地址
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {number} [options.opacity=1] - 透明度（0-1）
 * @returns {Promise<void>}
 */
async function drawTransparentGridPatternToCanvas(options) {
  const { ctx, imageUrl, canvasWidth, canvasHeight, opacity } = options || {};

  // 通用参数校验（不校验 layerInfo）
  validateDrawCanvasOptions(
    {
      ctx,
      canvasWidth,
      canvasHeight
    },
    'drawTransparentGridPatternToCanvas'
  );

  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new Error(
      'drawTransparentGridPatternToCanvas: imageUrl 不能为空且必须是字符串'
    );
  }

  const image = await loadImage(imageUrl);
  ctx.save();

  const finalOpacity = typeof opacity === 'number' ? opacity : 1;
  const oldAlpha = ctx.globalAlpha;
  ctx.globalAlpha = finalOpacity;

  const pattern = ctx.createPattern(image, 'repeat');
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  }

  ctx.globalAlpha = oldAlpha;
  ctx.restore();
}

/**
 * 绘制单个 layer 到 canvas
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {Object} options.layer - layer 对象（支持所有类型：userImage、skuTemplate、skuMaterial、background）
 * @param {number} [options.layer.opacity] - 图层透明度（0-1），从 layer.opacity 读取，默认为 1
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {number} [options.scale=1] - 缩放比例，用于将 layerInfo 中的坐标和尺寸缩放到目标画布
 * @returns {Promise<void>}
 */
export async function drawLayerToCanvas(options) {
  const { ctx, layer, canvasWidth, canvasHeight, scale = 1 } = options;

  // 验证 layer 特定参数
  if (!layer || typeof layer !== 'object') {
    throw new Error('drawLayerToCanvas: layer 参数必须是一个对象');
  }

  if (!layer.imageUrl || typeof layer.imageUrl !== 'string') {
    throw new Error('drawLayerToCanvas: layer.imageUrl 不能为空且必须是字符串');
  }

  try {
    // 透明网格底图：平铺填满（忽略 layerInfo，避免因 layerInfo 不一致导致显示异常）
    if (layer.type === constant.layer_transparentGrid) {
      const opacity = layer.opacity || 1;
      await drawTransparentGridPatternToCanvas({
        ctx,
        imageUrl: layer.imageUrl,
        canvasWidth,
        canvasHeight,
        opacity
      });
      return;
    }

    // 验证通用参数（包含 layerInfo 验证）
    validateDrawCanvasOptions(
      {
        ctx,
        canvasWidth,
        canvasHeight,
        layerInfo: layer.layerInfo
      },
      'drawLayerToCanvas'
    );

    const layerInfo = layer.layerInfo;

    // 加载图片
    const image = await loadImage(layer.imageUrl);
    // || {
    //   x: 0,
    //   y: 0,
    //   width: canvasWidth,
    //   height: canvasHeight,
    //   rotate: 0,
    //   scale: 1
    // };

    // 保存 canvas 状态
    ctx.save();
    // 设置透明度（从 layer.opacity 读取，默认为 1）
    const opacity = typeof layer.opacity === 'number' ? layer.opacity : 1;
    const oldAlpha = ctx.globalAlpha;
    ctx.globalAlpha = opacity;

    // 计算绘制位置和尺寸（应用缩放参数）
    const {
      left,
      top,
      width,
      height,
      angle,
      scaleX,
      scaleY,
      originX = 'left',
      originY = 'top'
    } = layerInfo;

    // 应用缩放参数将实际尺寸转换为显示尺寸
    const scaledLeft = left * scale;
    const scaledTop = top * scale;
    const scaledWidth = width * (scaleX || 1) * scale;
    const scaledHeight = height * (scaleY || 1) * scale;

    // 如果有旋转，根据 originX/originY 确定旋转中心点（与 Fabric.js 保持一致）
    if (angle !== 0) {
      // 计算变换原点位置（与 Fabric.js 的 originX/originY 对应）
      let originPointX = scaledLeft;
      let originPointY = scaledTop;

      if (originX === 'center') {
        originPointX = scaledLeft + scaledWidth / 2;
      } else if (originX === 'right') {
        originPointX = scaledLeft + scaledWidth;
      }

      if (originY === 'center') {
        originPointY = scaledTop + scaledHeight / 2;
      } else if (originY === 'bottom') {
        originPointY = scaledTop + scaledHeight;
      }

      // 平移到变换原点，旋转，然后平移回去
      ctx.translate(originPointX, originPointY);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.translate(-originPointX, -originPointY);
    }

    // 绘制图片
    ctx.drawImage(image, scaledLeft, scaledTop, scaledWidth, scaledHeight);

    // 恢复 canvas 状态（包括透明度）
    ctx.globalAlpha = oldAlpha;
    ctx.restore();
  } catch (error) {
    console.error('drawLayerToCanvas: 绘制 layer 失败', error);
    throw error;
  }
}

/**
 * 按照 zIndex 顺序绘制所有 layers 到 canvas
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {Array} options.layers - layer 数组
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {number} [options.scale=1] - 缩放比例，用于将 layerInfo 中的坐标和尺寸缩放到目标画布
 * @returns {Promise<void>}
 */
export async function drawLayerArrayToCanvas(options) {
  // 验证通用参数
  validateDrawCanvasOptions(options, 'drawLayerArrayToCanvas');

  const { ctx, layers, canvasWidth, canvasHeight, scale } = options;

  // 验证 layers 特定参数
  if (!layers || !Array.isArray(layers)) {
    throw new Error('drawLayerArrayToCanvas: layers 参数必须是一个数组');
  }

  // 如果没有 layers，直接返回
  if (layers.length === 0) {
    return;
  }

  // 按 zIndex 排序 layers（从小到大，zIndex 小的在下层）
  const sortedLayers = sortLayersByZIndex(layers);

  // 依次绘制每个 layer
  for (let i = 0; i < sortedLayers.length; i++) {
    const layer = sortedLayers[i];
    if (!layer || !layer.imageUrl) {
      continue;
    }

    try {
      await drawLayerToCanvas({
        ctx,
        layer,
        canvasWidth,
        canvasHeight,
        scale
      });
    } catch (error) {
      console.error(
        `drawLayerArrayToCanvas: 绘制 layer ${layer.id || i} 失败`,
        error
      );
      // 继续绘制其他 layer，不中断整个流程
    }
  }
}

/**
 * 添加 skuTemplate 类型的图层
 * @param {Object} options - 参数对象
 * @param {Object} options.layerData - 完整的 layerData 对象
 * @param {Object} options.template - 模板对象，包含 id、image、name 等字段
 * @param {Object} [options.layerInfo] - 图层画布信息（可选，如果不提供则根据 canvas 尺寸和模板图片尺寸计算）
 * @returns {Promise<Object>} 更新后的 layerData（不修改原对象）
 */
export async function addSkuTemplateLayer(options) {
  if (!options || typeof options !== 'object') {
    throw new Error('addSkuTemplateLayer: options 参数必须是一个对象');
  }

  const { layerData, template, layerInfo } = options;

  if (!layerData) {
    throw new Error('addSkuTemplateLayer: layerData 参数不能为空');
  }

  if (!template) {
    throw new Error('addSkuTemplateLayer: template 参数不能为空');
  }

  if (!template.image) {
    throw new Error('addSkuTemplateLayer: template.image 参数不能为空');
  }

  // 检查是否已存在 skuTemplate layer
  const existingLayer = getSkuTemplateLayer(layerData);
  if (existingLayer) {
    console.error(
      'addSkuTemplateLayer: 已存在 skuTemplate layer，无法重复添加'
    );
    return layerData;
  }

  // 如果没有提供 layerInfo，根据 canvas 和模板图片尺寸计算
  let finalLayerInfo = layerInfo;
  if (!finalLayerInfo) {
    if (!layerData.canvas) {
      throw new Error(
        'addSkuTemplateLayer: 未提供 layerInfo 且 layerData.canvas 不存在，无法创建 layerInfo'
      );
    }

    const canvasWidth = layerData.canvas.displayWidth;
    const canvasHeight = layerData.canvas.displayHeight;

    // 加载模板图片获取原始尺寸
    const imageElement = await loadImage(template.image);
    const templateImageWidth = imageElement.naturalWidth || imageElement.width;
    const templateImageHeight =
      imageElement.naturalHeight || imageElement.height;

    // 使用 calcImageInCanvasPosition 计算模板图片在画布中的位置（fill 模式，撑满画布）

    // 创建 layerInfo：width/height 使用图片原始尺寸，scale 根据显示尺寸计算
    finalLayerInfo = createLayerInfo({
      left: 0,
      top: 0,
      width: templateImageWidth,
      height: templateImageHeight,
      angle: 0,
      scaleX: canvasWidth / templateImageWidth,
      scaleY: canvasHeight / templateImageHeight
    });
  }

  // 创建 skuTemplate layer
  // zIndex 由外部（调用方）按规则计算后传入；这里直接使用传入值
  // - 若传入非数字，则默认置顶
  const skuTemplateZIndex =
    template && typeof template.zIndex === 'number'
      ? template.zIndex
      : maxLayerZIndex;

  const skuTemplateLayer = createLayer({
    type: constant.layer_template,
    imageUrl: template.image,
    layerInfo: finalLayerInfo,
    whiteInk: template.whiteInk,
    maskUrl: template.maskUrl,
    backUrl: template.backUrl,
    spotUv: template.spotUv,
    frostUrl: template.frostUrl,
    zIndex: skuTemplateZIndex
  });

  // 添加到 layerData
  return addLayer(layerData, skuTemplateLayer);
}

/**
 * 更新 skuTemplate 类型的图层（合并对象）
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {Object} updates - 要更新的字段对象，会与现有 layer 合并
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function updateSkuTemplateLayer(layerData, updates) {
  if (!layerData) {
    throw new Error('updateSkuTemplateLayer: layerData 参数不能为空');
  }

  if (!updates || typeof updates !== 'object') {
    throw new Error('updateSkuTemplateLayer: updates 参数必须是一个对象');
  }

  // 查找现有的 skuTemplate layer
  const existingLayer = getSkuTemplateLayer(layerData);
  if (!existingLayer) {
    throw new Error('updateSkuTemplateLayer: 未找到 skuTemplate layer');
  }

  // 合并现有 layer 和 updates 对象
  const mergedUpdates = {
    ...existingLayer,
    ...updates
  };

  // 调用 updateLayer 更新
  return updateLayer(layerData, existingLayer.id, mergedUpdates);
}

/**
 * 删除 skuTemplate 类型的图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function removeSkuTemplateLayer(layerData) {
  if (!layerData) {
    throw new Error('removeSkuTemplateLayer: layerData 参数不能为空');
  }

  // 查找现有的 skuTemplate layer
  const existingLayer = getSkuTemplateLayer(layerData);
  if (!existingLayer) {
    // 如果不存在，直接返回原 layerData（允许幂等操作）
    return layerData;
  }

  // 调用 removeLayer 删除
  return removeLayer(layerData, existingLayer.id);
}

/**
 * 将 layerInfo 转换为 Fabric.js 对象属性
 * @param {Object} layerInfo - layerInfo 对象
 * @returns {Object} Fabric.js 对象属性
 */
export function layerInfoToFabricProperties(layerInfo) {
  if (!layerInfo || typeof layerInfo !== 'object') {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo 参数必须是一个对象'
    );
  }

  // 验证必要字段
  if (typeof layerInfo.left !== 'number') {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.left 字段必须存在且为数字'
    );
  }

  if (typeof layerInfo.top !== 'number') {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.top 字段必须存在且为数字'
    );
  }

  if (typeof layerInfo.width !== 'number' || layerInfo.width <= 0) {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.width 字段必须存在且为大于0的数字'
    );
  }

  if (typeof layerInfo.height !== 'number' || layerInfo.height <= 0) {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.height 字段必须存在且为大于0的数字'
    );
  }

  // 获取缩放比例（默认 1）
  const scale =
    typeof layerInfo.scale === 'number' && layerInfo.scale > 0
      ? layerInfo.scale
      : 1;

  // 获取旋转角度（默认 0），支持 rotate 和 angle（兼容旧数据）
  const angle =
    typeof layerInfo.angle === 'number'
      ? layerInfo.angle
      : typeof layerInfo.rotate === 'number'
      ? layerInfo.rotate
      : 0;

  return {
    left: layerInfo.left,
    top: layerInfo.top,
    angle: angle,
    scaleX: scale,
    scaleY: scale,
    width: layerInfo.width,
    height: layerInfo.height,
    originX: 'left',
    originY: 'top'
  };
}

/**
 * 将 Fabric.js 对象属性转换为 layerInfo
 * @param {Object} fabricObj - Fabric.js 对象或包含属性的对象
 * @returns {Object} layerInfo 对象
 */
export function fabricPropertiesToLayerInfo(fabricObj) {
  if (!fabricObj || typeof fabricObj !== 'object') {
    throw new Error(
      'fabricPropertiesToLayerInfo: fabricObj 参数必须是一个对象'
    );
  }

  // 获取 Fabric 对象的属性
  const left = typeof fabricObj.left === 'number' ? fabricObj.left : 0;
  const top = typeof fabricObj.top === 'number' ? fabricObj.top : 0;
  const angle = typeof fabricObj.angle === 'number' ? fabricObj.angle : 0;
  const scaleX =
    typeof fabricObj.scaleX === 'number' && fabricObj.scaleX > 0
      ? fabricObj.scaleX
      : 1;
  const scaleY =
    typeof fabricObj.scaleY === 'number' && fabricObj.scaleY > 0
      ? fabricObj.scaleY
      : 1;
  const width =
    typeof fabricObj.width === 'number' && fabricObj.width > 0
      ? fabricObj.width
      : 1;
  const height =
    typeof fabricObj.height === 'number' && fabricObj.height > 0
      ? fabricObj.height
      : 1;

  // layerInfo 的 width/height 是原始尺寸（Fabric 的 width/height）
  return createLayerInfo({
    left: left,
    top: top,
    width: width,
    height: height,
    angle: angle,
    scaleX: scaleX,
    scaleY: scaleY
  });
}

/**
 * 将图片转换为白墨
 * 调用 fillNonTransparentPixels 将图片的非透明像素填充为白色
 * @param {string} imageUrl - 图片URL
 * @returns {Promise<string>} 白墨图片URL（base64 data URL）
 */
async function imageToWhiteInk(imageUrl) {
  let r = await canvasUtil.fillNonTransparentPixels({
    imageSrc: imageUrl,
    rgbaString: constant.whiteInkRgba
  });
  // mwx.debugLog({
  //   imageToWhiteInk: r
  // });
  return r;
}

/**
 * 使用 Fabric.js 加载所有 layers 到 canvas
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} [options.fabricCanvas] - Fabric canvas 实例（offscreen=true 时可不传，内部会创建离屏 StaticCanvas）
 * @param {Array} options.layers - layer 数组
 * @param {Object} [options.canvasInfo] - 画布信息（用于注入透明网格底图 layer，至少包含 displayWidth/displayHeight；offscreen=true 时必传）
 * @param {boolean} [options.clearCanvas=true] - 是否清空 canvas
 * @param {boolean} [options.selectFirst=false] - 是否选中第一个可编辑的对象
 * @param {string} [options.selectedLayerId] - 指定要选中的 layerId，优先级高于 selectFirst
 * @param {boolean} [options.canEdit] - 全局禁用编辑模式，如果为 false 会覆盖局部的 canEdit
 * @param {boolean} [options.drawWhiteInkOnly=false] - 是否只绘制白墨图层，如果为 true，只绘制 whiteInk != emptyWhiteInk 的图层，并将 imageUrl 替换为 whiteInk（whiteInk='' 时使用 imageToWhiteInk 转换）
 * @param {number} [options.highResolutionMultiplier] - 高清模式倍数，用于导出高清图片（默认 1）
 * @param {number} [options.scale=1] - 缩放比例，用于将 layerInfo 坐标从 displayWidth 缩放到当前 canvas 尺寸（默认 1）
 * @param {number} [options.offsetX=0] - 绘制坐标 X 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.offsetY=0] - 绘制坐标 Y 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.renderWidth] - 透明网格渲染宽度，不传则使用 Fabric canvas 宽度
 * @param {number} [options.renderHeight] - 透明网格渲染高度，不传则使用 Fabric canvas 高度
 * @param {boolean} [options.injectTransparentGrid=true] - 是否自动注入透明网格底图（仅 UI 展示，不参与导出）
 * @param {boolean} [options.offscreen=false] - 离屏模式：内部自建 StaticCanvas 渲染并导出 PNG dataURL，完成后自动 dispose；为 true 时 fabricCanvas 参数被忽略，canvasInfo 必传
 * @returns {Promise<Object|string>} offscreen=false 返回 layerId 到 Fabric 对象的映射；offscreen=true 返回导出 PNG 的 dataURL 字符串
 */
export async function loadLayersToFabricCanvas(options) {
  const {
    layers,
    canvasInfo,
    clearCanvas = true,
    selectFirst = false,
    selectedLayerId,
    canEdit,
    drawWhiteInkOnly = false,
    highResolutionMultiplier = 1,
    scale = 1,
    offsetX = 0,
    offsetY = 0,
    renderWidth,
    renderHeight,
    injectTransparentGrid = true,
    offscreen = false,
    callbacks
  } = options;

  if (!layers || !Array.isArray(layers)) {
    throw new Error('loadLayersToFabricCanvas: layers 参数必须是一个数组');
  }

  // offscreen=true：内部自建离屏 StaticCanvas，渲染后导出 dataURL 并 dispose
  let fabricCanvas = options.fabricCanvas;
  let internalOffscreenCanvas = null;
  if (offscreen) {
    if (
      !canvasInfo ||
      typeof canvasInfo.displayWidth !== 'number' ||
      typeof canvasInfo.displayHeight !== 'number' ||
      !(canvasInfo.displayWidth > 0) ||
      !(canvasInfo.displayHeight > 0)
    ) {
      throw new Error(
        'loadLayersToFabricCanvas: offscreen=true 时 canvasInfo.displayWidth/displayHeight 必须是合法正数'
      );
    }
    internalOffscreenCanvas = createOffscreenStaticCanvas({
      width: canvasInfo.displayWidth,
      height: canvasInfo.displayHeight
    });
    fabricCanvas = internalOffscreenCanvas;
  }

  if (!fabricCanvas) {
    throw new Error('loadLayersToFabricCanvas: fabricCanvas 参数不能为空');
  }

  // 将回调注册到 fabricCanvas 上，供 deleteObjectHandler/copyObjectHandler 读取
  // 必须在加载图层之前注册，因为 handler 在用户点击控件时从 canvas._layerControlCallbacks 取回调
  if (callbacks) {
    fabricCanvas._layerControlCallbacks = callbacks;
  }

  const layerObjects = {};
  const layerObjectList = [];

  // 透明网格底图（仅 UI 展示）
  // - 只有在提供 canvasInfo 且注入开关开启时才会注入
  // - drawWhiteInkOnly（导出白墨）时不注入，避免影响导出
  let renderLayers = layers;
  if (injectTransparentGrid && drawWhiteInkOnly !== true) {
    renderLayers = ensureTransparentGridLayer(renderLayers, canvasInfo);
  }

  // 按 zIndex 排序 layers
  let sortedLayers = sortLayersByZIndex(renderLayers);

  // 如果只绘制白墨图层，筛选并替换 imageUrl
  if (drawWhiteInkOnly) {
    // 先收集需要转换的图层和对应的转换 Promise
    const layersToProcess = [];
    const whiteInkPromises = [];

    for (let i = 0; i < sortedLayers.length; i++) {
      const layer = sortedLayers[i];
      // 筛选出 whiteInk != emptyWhiteInk 的图层（排除清空状态）
      if (
        layer.whiteInk != emptyWhiteInk &&
        !noWhiteLayerType.includes(layer.type)
      ) {
        layersToProcess.push(layer);
        // 如果 whiteInk=''，需要转换；否则直接使用
        if (!layer.whiteInk) {
          whiteInkPromises.push(imageToWhiteInk(layer.imageUrl));
        } else {
          whiteInkPromises.push(Promise.resolve(layer.whiteInk));
        }
      }
    }

    // 并发执行所有转换
    const whiteInkUrls = await Promise.all(whiteInkPromises);

    // 使用生成好的结果构建 whiteInkLayers
    const whiteInkLayers = [];
    for (let i = 0; i < layersToProcess.length; i++) {
      whiteInkLayers.push({
        ...layersToProcess[i],
        imageUrl: whiteInkUrls[i]
      });
    }

    sortedLayers = whiteInkLayers;
  }

  // 找到第一个可编辑的 layer
  let firstEditableLayer = null;

  // 预加载阶段：创建所有 Fabric 对象
  // - clearCanvas=true：保持旧画面不变，等全部对象 ready 后再一次性替换，避免空白帧
  // - clearCanvas=false：允许在现有画面基础上追加绘制
  const prevRenderOnAddRemove = fabricCanvas.renderOnAddRemove;
  const shouldRestoreRenderOnAddRemove =
    typeof prevRenderOnAddRemove === 'boolean' ||
    prevRenderOnAddRemove === undefined;
  try {
    if (prevRenderOnAddRemove !== false) {
      fabricCanvas.renderOnAddRemove = false;
    }

    // 依次加载每个 layer
    for (let i = 0; i < sortedLayers.length; i++) {
      const layer = sortedLayers[i];
      if (!layer || !layer.imageUrl) {
        continue;
      }

      try {
        // 如果传入了 selectedLayerId，只有该图层可编辑，其他图层强制不可编辑
        // 如果没有传入 selectedLayerId，使用原来的 canEdit 逻辑
        let layerCanEdit;
        if (selectedLayerId) {
          // 传入了 selectedLayerId：只有该图层可编辑，其他强制不可编辑
          layerCanEdit = layer.id === selectedLayerId ? true : false;
        } else {
          // 没有传入 selectedLayerId：使用原来的逻辑
          layerCanEdit = canEdit;
        }

        const fabricObj = await loadSingleLayerToFabricCanvas({
          fabricCanvas,
          layer,
          canEdit: layerCanEdit,
          highResolutionMultiplier,
          scale,
          offsetX,
          offsetY,
          renderWidth,
          renderHeight,
          callbacks
        });

        layerObjects[layer.id] = fabricObj;
        layerObjectList.push(fabricObj);

        // clearCanvas=false：追加绘制（保持原有行为：在现有 canvas 上叠加）
        if (clearCanvas !== true) {
          fabricCanvas.add(fabricObj);
        }

        // 记录第一个可编辑的 layer（如果全局 canEdit 为 false，则不记录）
        if (
          !firstEditableLayer &&
          canEdit !== false &&
          layer.canEditImage !== false
        ) {
          firstEditableLayer = layer;
        }
      } catch (error) {
        console.error(`加载 layer ${layer.id} 失败:`, error);
      }
    }

    // clearCanvas=true：原子替换（remove 旧对象 + add 新对象 + 最终一次 renderAll）
    if (clearCanvas === true) {
      try {
        if (typeof fabricCanvas.discardActiveObject === 'function') {
          fabricCanvas.discardActiveObject();
        }

        const oldObjects =
          typeof fabricCanvas.getObjects === 'function'
            ? fabricCanvas.getObjects()
            : [];
        const oldObjectList =
          oldObjects && typeof oldObjects.slice === 'function'
            ? oldObjects.slice()
            : [];
        for (let i = 0; i < oldObjectList.length; i++) {
          fabricCanvas.remove(oldObjectList[i]);
        }

        for (let i = 0; i < layerObjectList.length; i++) {
          fabricCanvas.add(layerObjectList[i]);
        }
      } catch (error) {
        console.error('loadLayersToFabricCanvas: 原子替换失败', error);
        throw error;
      }
    }
  } finally {
    // 恢复 renderOnAddRemove（避免中途异常导致后续画布不再自动重绘）
    if (shouldRestoreRenderOnAddRemove) {
      if (typeof prevRenderOnAddRemove === 'boolean') {
        fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
      } else if (prevRenderOnAddRemove === undefined) {
        // Fabric 默认行为基本等同于 true
        fabricCanvas.renderOnAddRemove = true;
      } else {
        fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
      }
    }
  }

  // 选中对象：优先使用 selectedLayerId，如果没有则使用 selectFirst
  if (selectedLayerId && layerObjects[selectedLayerId]) {
    fabricCanvas.setActiveObject(layerObjects[selectedLayerId]);
  } else if (
    selectFirst &&
    firstEditableLayer &&
    layerObjects[firstEditableLayer.id]
  ) {
    fabricCanvas.setActiveObject(layerObjects[firstEditableLayer.id]);
  }

  // 最终渲染 canvas
  fabricCanvas.renderAll();

  // offscreen 模式：导出 PNG dataURL 后销毁内部离屏画布，返回 dataURL
  if (offscreen && internalOffscreenCanvas) {
    try {
      const dataURL = internalOffscreenCanvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: highResolutionMultiplier
      });
      return dataURL;
    } finally {
      try {
        internalOffscreenCanvas.dispose();
      } catch (e) {
        /* noop */
      }
    }
  }

  return layerObjects;
}

/**
 * 加载单个 layer 到 Fabric canvas
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {Object} options.layer - layer 对象
 * @param {boolean} [options.canEdit] - 全局禁用编辑模式，如果为 false 会覆盖局部的 canEdit
 * @param {number} [options.highResolutionMultiplier] - 高清模式倍数，用于导出高清图片（默认 1，建议 2-3）
 * @param {number} [options.scale=1]
 * @param {number} [options.offsetX=0] - 绘制坐标 X 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.offsetY=0] - 绘制坐标 Y 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.renderWidth] - 透明网格渲染宽度
 * @param {number} [options.renderHeight] - 透明网格渲染高度
 * @returns {Promise<fabric.Image>} Fabric Image 对象
 */
export async function loadSingleLayerToFabricCanvas(options) {
  const {
    fabricCanvas,
    layer,
    canEdit,
    highResolutionMultiplier,
    scale = 1,
    offsetX = 0,
    offsetY = 0,
    renderWidth,
    renderHeight,
    callbacks
  } = options;

  if (!fabricCanvas) {
    throw new Error('loadSingleLayerToFabricCanvas: fabricCanvas 参数不能为空');
  }

  if (!layer || !layer.imageUrl) {
    throw new Error('loadSingleLayerToFabricCanvas: layer 参数无效');
  }

  // 透明网格底图：使用 Pattern + Rect 平铺绘制（不参与导出）
  if (layer.type === constant.layer_transparentGrid) {
    return await loadTransparentGridLayerToFabricCanvas({
      fabricCanvas,
      layer,
      scale,
      offsetX,
      offsetY,
      renderWidth,
      renderHeight
    });
  }

  let imageUrlToLoad = layer.imageUrl;
  // 背景图层默认做灰度转 alpha（同色卡纸需要）；
  // layer.skipGrayToAlpha=true 时跳过（sticker 背景图片场景，保持图片原色不透明）
  if (layer.type === constant.layer_background && !layer.skipGrayToAlpha) {
    imageUrlToLoad = await convertGrayToAlphaForBackground(layer.imageUrl);
    // mwx.debugLog({
    //   title: 'convertGrayToAlphaForBackground',
    //   imageUrlToLoad,
    //   layerImage: layer.imageUrl
    // });
  }

  let timeout = null;
  try {
    const loadPromise = FabricImage.fromURL(imageUrlToLoad, {
      crossOrigin: 'anonymous'
    });

    const img = await loadPromise;

    const layerInfo = layer.layerInfo || {};
    // 处理 canEdit 参数：
    // - canEdit === true: 强制可编辑（覆盖 layer.canEditImage）
    // - canEdit === false: 强制不可编辑（覆盖 layer.canEditImage）
    // - canEdit === undefined: 使用 layer.canEditImage 的默认值
    let finalCanEdit;
    if (canEdit === true) {
      finalCanEdit = true; // 强制可编辑
    } else if (canEdit === false) {
      finalCanEdit = false; // 强制不可编辑
    } else {
      finalCanEdit = layer.canEditImage !== false; // 使用layer的默认值
    }

    // 读取透明度（从 layer.opacity 读取，默认为 1）
    const opacity = typeof layer.opacity === 'number' ? layer.opacity : 1;
    // debugger;
    // 应用 scale 缩放坐标（layerInfo 基于 displayWidth，需要缩放到当前 canvas 尺寸）
    img.set({
      left: layerInfo.left + offsetX,
      top: layerInfo.top + offsetY,
      scaleX: layerInfo.scaleX,
      scaleY: layerInfo.scaleY,
      angle: layerInfo.angle || 0,
      originX: layerInfo.originX || 'left',
      originY: layerInfo.originY || 'top',
      opacity: opacity, // 设置透明度
      selectable: finalCanEdit,
      hasControls: finalCanEdit,
      hasBorders: finalCanEdit,
      evented: finalCanEdit,
      lockMovementX: !finalCanEdit,
      lockMovementY: !finalCanEdit,
      lockRotation: !finalCanEdit,
      lockScalingX: !finalCanEdit,
      lockScalingY: !finalCanEdit,
      lockUniScaling: true, // 锁定统一缩放，强制保持宽高比不变
      // editImageNew：缩放以对角点为锚点（类似 PS 默认缩放行为）
      centeredScaling: false
    });

    // editImageNew：可编辑对象定制 controls（左上删除、右上复制、右下缩放）
    // 注意：applyLayerCustomControls 在下方统一调用（带 callbacks），此处不再单独调用

    // 存储 layerId
    img.set('layerId', layer.id);
    img.set('strokeWidth', layer.strokeWidth || 0);
    // 存储高清倍数，用于导出时使用
    if (highResolutionMultiplier && highResolutionMultiplier > 1) {
      img.set('highResolutionMultiplier', highResolutionMultiplier);
    }

    // 更新对象的交互边界（必须在设置属性后调用，否则拖拽等操作无法正常工作）
    img.setCoords();

    // 可编辑对象定制 controls + 手势（在 layerId 设置之后调用，确保 handler 能拿到 layerId）
    if (finalCanEdit === true) {
      if (layer.isSticker) {
        // 贴纸类型自定义操作手柄
        applyLayerCustomControls(img, callbacks);
      } else {
        applyEditImageCornerControls(img); // 原逻辑
      }
      // 可编辑对象出现时，确保双指缩放/旋转/平移手势处于开启状态（默认即为 true）
      setActiveObjectPinchGestureEnabled(true);
    }

    // 注意：这里不再 add/render，由调用方统一处理，避免 clearCanvas 时出现空白帧与频繁重绘
    return img;
  } catch (error) {
    throw error;
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

/**
 * 使用 Fabric.js 加载包含白墨的图层并合成
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {Array} options.layers - layer 数组
 * @param {Object} options.canvasInfo - 画布信息对象（包含 displayWidth 和 displayHeight）
 * @param {number} [options.opacity] - 非白墨区域的透明度（可选，默认从 config.imageWithWhiteInkDefaultOpacity 获取）
 * @returns {Promise<void>}
 */
export async function loadLayersToFabricCanvasWithWhiteInk(options) {
  const {
    fabricCanvas,
    layers,
    canvasInfo,
    opacity,
    highResolutionMultiplier,
    injectTransparentGrid = true,
    imageOpacity,
    whiteMode
  } = options;

  if (!fabricCanvas) {
    throw new Error(
      'loadLayersToFabricCanvasWithWhiteInk: fabricCanvas 参数不能为空'
    );
  }

  if (!layers || !Array.isArray(layers)) {
    throw new Error(
      'loadLayersToFabricCanvasWithWhiteInk: layers 参数必须是一个数组'
    );
  }

  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    throw new Error(
      'loadLayersToFabricCanvasWithWhiteInk: canvasInfo 参数必须包含 displayWidth 和 displayHeight'
    );
  }

  // 使用传入的 opacity 或默认值
  const finalOpacity =
    typeof opacity === 'number' ? opacity : imageWithWhiteInkDefaultOpacity;

  try {
    // 透明网格底图（仅 UI 展示）
    // - 只有在注入开关开启时才会注入
    let layersWithTransparentGrid = layers;
    if (injectTransparentGrid) {
      layersWithTransparentGrid = ensureTransparentGridLayer(
        layers,
        canvasInfo
      );
    }

    // 检查是否存在用户自定义白墨图层
    const hasCustomWhiteInkLayers = layersWithTransparentGrid.some(
      (layer) =>
        layer &&
        !noWhiteLayerType.includes(layer.type) &&
        layer.whiteInk &&
        layer.whiteInk != emptyWhiteInk
    );

    // 检查是否存在默认白墨图层，默认白墨不生成独立白墨图
    const hasDefaultWhiteInkLayers = layersWithTransparentGrid.some(
      (layer) =>
        layer &&
        !noWhiteLayerType.includes(layer.type) &&
        (layer.whiteInk === undefined ||
          layer.whiteInk === '' ||
          layer.whiteInk === emptyWhiteInk) &&
        layer.imageUrl
    );

    const hasWhiteInkLayers =
      hasCustomWhiteInkLayers || hasDefaultWhiteInkLayers;

    if (hasWhiteInkLayers) {
      // 离屏 StaticCanvas：用于导出 nonBackground / whiteInk / userImageWhiteInk
      // 说明：主画布属于 UI 展示，不应在中间导出步骤被临时替换成白墨图等中间态
      const offscreenCanvas = createOffscreenStaticCanvas({
        width: canvasInfo.displayWidth,
        height: canvasInfo.displayHeight
      });

      // 第一步：绘制不包含背景图的图层
      const nonBackgroundLayers = layersWithTransparentGrid.filter(
        (layer) => layer && layer.type !== constant.layer_transparentGrid
      );

      //
      await loadLayersToFabricCanvas({
        fabricCanvas: offscreenCanvas,
        layers: nonBackgroundLayers,
        canvasInfo: canvasInfo,
        clearCanvas: true,
        selectFirst: false,
        canEdit: false,
        highResolutionMultiplier,
        injectTransparentGrid: false
      });

      // 导出不包含背景图的图层为图片
      const nonBackgroundImageDataURL = offscreenCanvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: highResolutionMultiplier
      });

      let whiteInkImageDataURL;
      let finalWhiteInkImageDataURL;

      // 只有存在真实白墨图时才导出白墨图层，默认白墨不生成独立白墨图
      if (hasCustomWhiteInkLayers) {
        // 第二步：只绘制白墨图层
        await loadLayersToFabricCanvas({
          fabricCanvas: offscreenCanvas,
          layers: layersWithTransparentGrid,
          canvasInfo: canvasInfo,
          clearCanvas: true,
          selectFirst: false,
          canEdit: false,
          drawWhiteInkOnly: true,
          highResolutionMultiplier,
          injectTransparentGrid: false
        });

        // 导出白墨图层为图片
        whiteInkImageDataURL = offscreenCanvas.toDataURL({
          format: 'png',
          quality: 1,
          multiplier: highResolutionMultiplier
        });

        // 若满足条件：存在白墨图层(userImage和template图层) + 模板层 zIndex 大于所有 userImage 的 zIndex + 模板存在 imageUrl
        // 则对白墨导出图按 imageUrl 的透明区域裁剪（仅透明区可见）
        finalWhiteInkImageDataURL = whiteInkImageDataURL;
        try {
          const cropInfo = getWhiteInkMaskTransparentCropInfo(
            layersWithTransparentGrid
          );
          if (cropInfo && cropInfo.imageUrl) {
            // 触发裁剪时：只对 userImage 白墨做裁剪，template 白墨不裁剪，后续再叠加回去
            let cropWhiteInkImageDataURL = whiteInkImageDataURL;
            try {
              const userImageLayers = layersWithTransparentGrid.filter(
                (layer) => layer && layer.type === constant.layer_userImage
              );
              if (userImageLayers.length > 0) {
                await loadLayersToFabricCanvas({
                  fabricCanvas: offscreenCanvas,
                  layers: userImageLayers,
                  canvasInfo: canvasInfo,
                  clearCanvas: true,
                  selectFirst: false,
                  canEdit: false,
                  drawWhiteInkOnly: true,
                  highResolutionMultiplier,
                  injectTransparentGrid: false
                });

                cropWhiteInkImageDataURL = offscreenCanvas.toDataURL({
                  format: 'png',
                  quality: 1,
                  multiplier: highResolutionMultiplier
                });
              }
            } catch (error) {
              // user 白墨导出失败时降级为原白墨导出结果，避免影响现有流程
              console.error(
                'loadLayersToFabricCanvasWithWhiteInk: user 白墨导出失败，已降级为原结果',
                error
              );
              cropWhiteInkImageDataURL = whiteInkImageDataURL;
            }

            let overlayTemplateWhiteInk;
            // emptyWhiteInk 表示“清空态”，不叠加任何模板白墨
            if (cropInfo.whiteInk == emptyWhiteInk) {
              overlayTemplateWhiteInk = undefined;
            } else if (cropInfo.whiteInk) {
              // 直接使用已有模板白墨
              overlayTemplateWhiteInk = cropInfo.whiteInk;
            } else {
              // whiteInk 为空：用模板图生成一张“黑色白墨”作为兜底叠加（带缓存）
              try {
                overlayTemplateWhiteInk = await imageToWhiteInk(
                  cropInfo.imageUrl
                );
              } catch (error) {
                console.error(
                  'loadLayersToFabricCanvasWithWhiteInk: 生成模板黑色白墨失败，已降级为不叠加',
                  error
                );
                overlayTemplateWhiteInk = undefined;
              }
            }

            finalWhiteInkImageDataURL = await cropWhiteInkByMaskTransparentArea(
              {
                whiteInkImageDataURL: cropWhiteInkImageDataURL,
                imageUrl: cropInfo.imageUrl,
                canvasWidth: canvasInfo.displayWidth,
                canvasHeight: canvasInfo.displayHeight,
                threshold: 16,
                whiteInk: overlayTemplateWhiteInk
              }
            );
            // mwx.debugLog({
            //   finalWhiteInkImageDataURL
            // });
          }
        } catch (error) {
          // 裁剪失败时降级为原白墨导出结果，避免影响现有流程
          console.error(
            'loadLayersToFabricCanvasWithWhiteInk: 白墨裁剪失败，已降级为原结果',
            error
          );
        }
      }
      let whiteInkImageSrc =
        hasDefaultWhiteInkLayers && !finalWhiteInkImageDataURL
          ? whiteMode == constant.whiteModeMap.EMPTY
            ? constant.whiteInkTypeMap.EMPTY
            : constant.whiteInkTypeMap.DEFAULT
          : finalWhiteInkImageDataURL;

      // 清空白墨时生成同画布尺寸的纯透明图，避免后续合成逻辑直接消费 EMPTY 哨兵值
      if (whiteInkImageSrc == constant.whiteInkTypeMap.EMPTY) {
        whiteInkImageSrc = await getTransparentBlobUrlBySize({
          width: canvasInfo.displayWidth,
          height: canvasInfo.displayHeight
        });
      } else if (
        whiteInkImageSrc == constant.whiteInkTypeMap.DEFAULT ||
        whiteInkImageSrc == '' ||
        whiteInkImageSrc === undefined
      ) {
        await loadLayersToFabricCanvas({
          fabricCanvas: offscreenCanvas,
          layers: layersWithTransparentGrid,
          canvasInfo: canvasInfo,
          clearCanvas: true,
          selectFirst: false,
          canEdit: false,
          drawWhiteInkOnly: true,
          highResolutionMultiplier,
          injectTransparentGrid: false
        });

        // 导出白墨图层为图片
        whiteInkImageSrc = offscreenCanvas.toDataURL({
          format: 'png',
          quality: 1,
          multiplier: highResolutionMultiplier
        });
      }

      // 第三步：合成两张图片
      let compositeImageDataURL = await canvasUtil.drawImageWithGrayAlpha({
        whiteInkImageSrc,
        outlineImageSrc: nonBackgroundImageDataURL,
        canvasWidth: canvasInfo.displayWidth,
        canvasHeight: canvasInfo.displayHeight,
        opacity: imageOpacity,
        exportImage: true
      });

      // if (imageOpacity !== undefined) {
      //   compositeImageDataURL = await canvasUtil.setImageOpacity({
      //     imageSrc: compositeImageDataURL,
      //     opacity: imageOpacity
      //   });
      // }

      mwx.debugLog({
        title: '白墨合成',
        whiteInkImageSrc,
        nonBackgroundImageDataURL,
        compositeImageDataURL
      });

      // 第四步：预加载下一帧对象（透明网格 + 背景 + 合成图），全部 ready 后再原子替换
      // - 避免中途 clear() 造成空白帧闪屏
      // - 若合成图加载失败/超时：抛错并保持旧画面不变（由上层提示）

      // 最底层：透明网格底图（UI 展示用，不参与导出合成）
      const transparentGridLayers = layersWithTransparentGrid.filter((layer) =>
        isTransparentGridLayer(layer)
      );
      const transparentGridObjects = await Promise.all(
        transparentGridLayers.map((layer) => {
          return loadSingleLayerToFabricCanvas({
            fabricCanvas: fabricCanvas,
            layer: layer,
            canEdit: false,
            highResolutionMultiplier,
            scale: 1
          });
        })
      );

      // 背景图层
      // const backgroundLayers = layersWithTransparentGrid.filter(
      //   (layer) => layer && layer.type === constant.layer_background
      // );
      // const backgroundObjects = await Promise.all(
      //   backgroundLayers.map((layer) => {
      //     return loadSingleLayerToFabricCanvas({
      //       fabricCanvas: fabricCanvas,
      //       layer: layer,
      //       canEdit: false,
      //       highResolutionMultiplier,
      //       scale: 1
      //     });
      //   })
      // );

      // 合成图层（在背景之上）
      const compositeObject = await new Promise((resolve, reject) => {
        let timeout = setTimeout(() => {
          reject(new Error('合成图片加载超时'));
        }, 10000);

        FabricImage.fromURL(compositeImageDataURL, {
          crossOrigin: 'anonymous'
        })
          .then((img) => {
            if (timeout) {
              clearTimeout(timeout);
              timeout = null;
            }
            try {
              // 计算缩放比例，使图片填满画布（保持与原逻辑一致：按 displayWidth/displayHeight）
              const scaleX = canvasInfo.displayWidth / img.width;
              const scaleY = canvasInfo.displayHeight / img.height;

              img.set({
                left: 0,
                top: 0,
                scaleX: scaleX,
                scaleY: scaleY,
                angle: 0,
                originX: 'left',
                originY: 'top',
                selectable: false,
                hasControls: false,
                hasBorders: false,
                evented: false
              });
              resolve(img);
            } catch (error) {
              reject(error);
            }
          })
          .catch((error) => {
            if (timeout) {
              clearTimeout(timeout);
              timeout = null;
            }
            reject(error);
          });
      });

      const nextObjectList = []
        .concat(transparentGridObjects)
        // .concat(backgroundObjects)
        .concat([compositeObject]);

      // 原子替换（remove 旧对象 + add 新对象 + 最终一次 renderAll）
      const prevRenderOnAddRemove = fabricCanvas.renderOnAddRemove;
      const shouldRestoreRenderOnAddRemove =
        typeof prevRenderOnAddRemove === 'boolean' ||
        prevRenderOnAddRemove === undefined;
      try {
        if (prevRenderOnAddRemove !== false) {
          fabricCanvas.renderOnAddRemove = false;
        }

        if (typeof fabricCanvas.discardActiveObject === 'function') {
          fabricCanvas.discardActiveObject();
        }

        const oldObjects =
          typeof fabricCanvas.getObjects === 'function'
            ? fabricCanvas.getObjects()
            : [];
        const oldObjectList =
          oldObjects && typeof oldObjects.slice === 'function'
            ? oldObjects.slice()
            : [];
        for (let i = 0; i < oldObjectList.length; i++) {
          fabricCanvas.remove(oldObjectList[i]);
        }

        for (let i = 0; i < nextObjectList.length; i++) {
          fabricCanvas.add(nextObjectList[i]);
        }
      } finally {
        // 恢复 renderOnAddRemove（避免中途异常导致后续画布不再自动重绘）
        if (shouldRestoreRenderOnAddRemove) {
          if (typeof prevRenderOnAddRemove === 'boolean') {
            fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
          } else if (prevRenderOnAddRemove === undefined) {
            fabricCanvas.renderOnAddRemove = true;
          } else {
            fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
          }
        }
      }

      // 最终渲染
      fabricCanvas.renderAll();
    } else {
      // 如果没有白墨图层，使用原有逻辑
      // 第一步：绘制所有图层
      await loadLayersToFabricCanvas({
        fabricCanvas: fabricCanvas,
        layers: layersWithTransparentGrid,
        canvasInfo: canvasInfo,
        clearCanvas: true,
        selectFirst: false,
        canEdit: false,
        highResolutionMultiplier,
        injectTransparentGrid: false
      });

      // 此时 fabricCanvas 已经包含了所有图层，无需额外处理
    }
  } catch (error) {
    console.error('loadLayersToFabricCanvasWithWhiteInk: 处理失败', error);
    throw error;
  }
}

/**
 * 导出最终白墨图片,导出尺寸和 layerData.canvasInfo 的比例 一致
 * 提取白墨图层并导出为 dataURL，包含裁剪处理逻辑
 *
 * @param {Object} options - 参数对象
 * @param {Array} options.layers - 图层数组
 * @param {Object} options.canvasInfo - 画布信息对象（包含 displayWidth 和 displayHeight）
 * @param {number} [options.highResolutionMultiplier=1] - 高清倍数，用于导出高清图片（默认 1）
 * @param {boolean} [options.reverseCompareGate=false] - 为 true 时：用「whiteInk != emptyWhiteInk」判定是否导出（含 whiteInk 为空串时用 imageUrl 转白墨），用于反面/白墨像素对比；默认 false 保持原 isDefaultWhiteInk 口径
 * @returns {Promise<string>} 返回最终白墨图片的 dataURL，如果没有白墨图层则返回空字符串
 */
export async function exportFinalWhiteInk(options) {
  const {
    layers,
    canvasInfo,
    highResolutionMultiplier = 1,
    reverseCompareGate = false
  } = options || {};

  // 参数验证
  if (!layers || !Array.isArray(layers)) {
    throw new Error('exportFinalWhiteInk: layers 参数必须是一个数组');
  }

  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    throw new Error(
      'exportFinalWhiteInk: canvasInfo 参数必须包含 displayWidth 和 displayHeight'
    );
  }

  // 确保 UI 侧存在透明网格底图（不参与导出合成）
  const layersWithTransparentGrid = ensureTransparentGridLayer(
    layers,
    canvasInfo
  );

  // 检查是否有白墨图层（对比场景下包含「whiteInk 为空串 → drawWhiteInkOnly 用 imageUrl」的 userImage）
  const hasWhiteInkLayers = reverseCompareGate
    ? layersWithTransparentGrid.some(function (layer) {
        return (
          layer &&
          layer.whiteInk != emptyWhiteInk &&
          !noWhiteLayerType.includes(layer.type)
        );
      })
    : layersWithTransparentGrid.some(
        (layer) => layer && !isDefaultWhiteInk(layer)
      );

  if (!hasWhiteInkLayers) {
    // 如果没有白墨图层，返回空字符串
    return '';
  }

  try {
    // 创建离屏 StaticCanvas：用于导出白墨
    const offscreenCanvas = createOffscreenStaticCanvas({
      width: canvasInfo.displayWidth,
      height: canvasInfo.displayHeight
    });

    // 绘制白墨图层
    await loadLayersToFabricCanvas({
      fabricCanvas: offscreenCanvas,
      layers: layersWithTransparentGrid,
      canvasInfo: canvasInfo,
      clearCanvas: true,
      selectFirst: false,
      canEdit: false,
      drawWhiteInkOnly: true,
      highResolutionMultiplier,
      injectTransparentGrid: false
    });

    // 导出白墨图层为图片
    const whiteInkImageDataURL = offscreenCanvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: highResolutionMultiplier
    });

    // 若满足条件：存在白墨图层(userImage和template图层) + 模板层 zIndex 大于所有 userImage 的 zIndex + 模板存在 imageUrl
    // 则对白墨导出图按 imageUrl 的透明区域裁剪（仅透明区可见）
    let finalWhiteInkImageDataURL = whiteInkImageDataURL;
    try {
      const cropInfo = getWhiteInkMaskTransparentCropInfo(
        layersWithTransparentGrid
      );
      if (cropInfo && cropInfo.imageUrl) {
        // 触发裁剪时：只对 userImage 白墨做裁剪，template 白墨不裁剪，后续再叠加回去
        let cropWhiteInkImageDataURL = whiteInkImageDataURL;
        try {
          const userImageLayers = layersWithTransparentGrid.filter(
            (layer) => layer && layer.type === constant.layer_userImage
          );
          if (userImageLayers.length > 0) {
            await loadLayersToFabricCanvas({
              fabricCanvas: offscreenCanvas,
              layers: userImageLayers,
              canvasInfo: canvasInfo,
              clearCanvas: true,
              selectFirst: false,
              canEdit: false,
              drawWhiteInkOnly: true,
              highResolutionMultiplier,
              injectTransparentGrid: false
            });

            cropWhiteInkImageDataURL = offscreenCanvas.toDataURL({
              format: 'png',
              quality: 1,
              multiplier: highResolutionMultiplier
            });
          }
        } catch (error) {
          // user 白墨导出失败时降级为原白墨导出结果，避免影响现有流程
          console.error(
            'exportFinalWhiteInk: user 白墨导出失败，已降级为原结果',
            error
          );
          cropWhiteInkImageDataURL = whiteInkImageDataURL;
        }

        let overlayTemplateWhiteInk;
        // emptyWhiteInk 表示"清空态"，不叠加任何模板白墨
        if (cropInfo.whiteInk == emptyWhiteInk) {
          overlayTemplateWhiteInk = undefined;
        } else if (cropInfo.whiteInk) {
          // 直接使用已有模板白墨
          overlayTemplateWhiteInk = cropInfo.whiteInk;
        } else {
          // whiteInk 为空：用模板图生成一张"黑色白墨"作为兜底叠加（带缓存）
          try {
            overlayTemplateWhiteInk = await imageToWhiteInk(cropInfo.imageUrl);
          } catch (error) {
            console.error(
              'exportFinalWhiteInk: 生成模板黑色白墨失败，已降级为不叠加',
              error
            );
            overlayTemplateWhiteInk = undefined;
          }
        }

        finalWhiteInkImageDataURL = await cropWhiteInkByMaskTransparentArea({
          whiteInkImageDataURL: cropWhiteInkImageDataURL,
          imageUrl: cropInfo.imageUrl,
          canvasWidth: canvasInfo.displayWidth,
          canvasHeight: canvasInfo.displayHeight,
          threshold: 16,
          whiteInk: overlayTemplateWhiteInk
        });
        // mwx.debugLog({
        //   exportFinalWhiteInk: finalWhiteInkImageDataURL
        // });
      }
    } catch (error) {
      // 裁剪失败时降级为原白墨导出结果，避免影响现有流程
      console.error('exportFinalWhiteInk: 白墨裁剪失败，已降级为原结果', error);
    }

    return finalWhiteInkImageDataURL;
  } catch (error) {
    console.error('exportFinalWhiteInk: 处理失败', error);
    throw error;
  }
}

/** 反面非透明但白墨侧透明：占反面非透明像素比例超过此值（0–1）则阻断 */
export const REVERSE_WHITEINK_MISMATCH_RATIO_THRESHOLD = 0.05;
/** 反面非透明但白墨侧透明：像素数超过此值则阻断 */
export const REVERSE_WHITEINK_MISMATCH_PIXEL_THRESHOLD = 50000; // 暂时不用

/**
 * 将 dataURL 缩放到指定尺寸并返回 ImageData；空字符串视为全透明
 * @param {string} dataURL
 * @param {number} expectW
 * @param {number} expectH
 * @returns {Promise<ImageData>}
 */
function dataURLToImageDataForCompare(dataURL, expectW, expectH) {
  return new Promise(function (resolve, reject) {
    if (typeof document === 'undefined') {
      reject(new Error('dataURLToImageDataForCompare: document 不存在'));
      return;
    }
    const w = Math.max(0, Math.round(expectW));
    const h = Math.max(0, Math.round(expectH));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('dataURLToImageDataForCompare: 无法获取 2d context'));
      return;
    }
    if (!dataURL || dataURL === '') {
      resolve(ctx.getImageData(0, 0, w, h));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        ctx.drawImage(img, 0, 0, w, h);
        resolve(ctx.getImageData(0, 0, w, h));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = function (err) {
      reject(err || new Error('dataURLToImageDataForCompare: 图片加载失败'));
    };
    img.src = dataURL;
  });
}

/**
 * 反面合成图与白墨导出图同尺寸像素对比（alpha===0 为透明）。
 * 白墨导出使用 exportFinalWhiteInk + reverseCompareGate（whiteInk 空串时用 imageUrl 转白墨）。
 *
 * @param {Object} options
 * @param {Array} options.layers - 正面图层
 * @param {Array} [options.reverseLayer=[]] - 反面图层
 * @param {Object} options.canvasInfo - displayWidth/displayHeight
 * @param {number} [options.highResolutionMultiplier=1]
 * @param {boolean} [options.debug=false]
 * @param {number} [options.ratioThreshold]
 * @param {number} [options.pixelCountThreshold]
 * @param {string} [options.maskUrlFront] - 正面 mask（与 getMaskUrl 一致），有则对白墨导出图先裁剪再对比
 * @param {string} [options.maskUrlReverse] - 反面 mask（通常水平镜像），有则对反面导出图先裁剪再对比
 * @param {boolean} [options.excuteMaskClip=true]
 * @param {string} [options.bgColor] - 传给 applySkuTemplateMaskClipPath
 * @returns {Promise<{mismatchCount:number,reverseOpaqueCount:number,ratio:number,blocked:boolean,reverseWithoutWhite:{mismatchCount:number,reverseOpaqueCount:number,ratio:number,blocked:boolean},whiteWithoutReverse:{mismatchCount:number,whiteOpaqueCount:number,ratio:number,blocked:boolean},error?:*,debugWhiteInkDataUrl?:string,debugReverseDataUrl?:string,debugReverseMismatchOverlayDataUrl?:string}>}
 */
export async function compareReverseLayerAgainstWhiteInk(options) {
  const opts = options || {};
  const layers = opts.layers;
  const reverseLayer = Array.isArray(opts.reverseLayer)
    ? opts.reverseLayer
    : [];
  const canvasInfo = opts.canvasInfo;
  const highResolutionMultiplier =
    typeof opts.highResolutionMultiplier === 'number'
      ? opts.highResolutionMultiplier
      : 1;
  const debug = opts.debug === true;
  const ratioThreshold =
    typeof opts.ratioThreshold === 'number'
      ? opts.ratioThreshold
      : REVERSE_WHITEINK_MISMATCH_RATIO_THRESHOLD;
  const pixelCountThreshold =
    typeof opts.pixelCountThreshold === 'number'
      ? opts.pixelCountThreshold
      : REVERSE_WHITEINK_MISMATCH_PIXEL_THRESHOLD;

  const createEmptyCheckResult = function (opaqueCountField) {
    const result = {
      mismatchCount: 0,
      ratio: 0,
      blocked: false
    };
    result[opaqueCountField] = 0;
    return result;
  };
  const createEmptyResult = function () {
    const reverseWithoutWhite = createEmptyCheckResult('reverseOpaqueCount');
    const whiteWithoutReverse = createEmptyCheckResult('whiteOpaqueCount');
    return {
      mismatchCount: 0,
      reverseOpaqueCount: 0,
      ratio: 0,
      blocked: false,
      reverseWithoutWhite: reverseWithoutWhite,
      whiteWithoutReverse: whiteWithoutReverse
    };
  };

  try {
    if (!layers || !Array.isArray(layers)) {
      throw new Error(
        'compareReverseLayerAgainstWhiteInk: layers 参数必须是一个数组'
      );
    }
    if (
      !canvasInfo ||
      typeof canvasInfo.displayWidth !== 'number' ||
      typeof canvasInfo.displayHeight !== 'number'
    ) {
      throw new Error(
        'compareReverseLayerAgainstWhiteInk: canvasInfo 须含 displayWidth/displayHeight'
      );
    }

    const whiteDataURL = await exportFinalWhiteInk({
      layers: layers,
      canvasInfo: canvasInfo,
      highResolutionMultiplier: highResolutionMultiplier,
      reverseCompareGate: true
    });

    const offReverse = createOffscreenStaticCanvas({
      width: canvasInfo.displayWidth,
      height: canvasInfo.displayHeight
    });
    await loadLayersToFabricCanvas({
      fabricCanvas: offReverse,
      layers: reverseLayer,
      canvasInfo: canvasInfo,
      clearCanvas: true,
      selectFirst: false,
      canEdit: false,
      drawWhiteInkOnly: false,
      highResolutionMultiplier: highResolutionMultiplier,
      injectTransparentGrid: false
    });
    const reverseDataURL = offReverse.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: highResolutionMultiplier
    });

    const maskUrlFront =
      opts.maskUrlFront && typeof opts.maskUrlFront === 'string'
        ? opts.maskUrlFront
        : '';
    const maskUrlReverse =
      opts.maskUrlReverse && typeof opts.maskUrlReverse === 'string'
        ? opts.maskUrlReverse
        : '';
    const excuteMaskClip = opts.excuteMaskClip !== false;

    const clipCommon = {
      canvasInfo: canvasInfo,
      layers: layers,
      bgColor: opts.bgColor,
      highResolutionMultiplier: highResolutionMultiplier,
      excuteMaskClip: excuteMaskClip
    };

    let whiteForCompare = whiteDataURL;
    let reverseForCompare = reverseDataURL;
    if (maskUrlFront) {
      whiteForCompare = await applySkuMaskClipToDataURL(
        Object.assign({}, clipCommon, {
          dataURL: whiteDataURL,
          maskUrl: maskUrlFront
        })
      );
    }
    if (maskUrlReverse) {
      reverseForCompare = await applySkuMaskClipToDataURL(
        Object.assign({}, clipCommon, {
          dataURL: reverseDataURL,
          maskUrl: maskUrlReverse
        })
      );
    }

    reverseForCompare = await mirrorImageHorizontal(reverseForCompare);

    const wPx = Math.max(
      1,
      Math.round(canvasInfo.displayWidth * highResolutionMultiplier)
    );
    const hPx = Math.max(
      1,
      Math.round(canvasInfo.displayHeight * highResolutionMultiplier)
    );

    const whiteImageData = await dataURLToImageDataForCompare(
      whiteForCompare,
      wPx,
      hPx
    );
    const reverseImageData = await dataURLToImageDataForCompare(
      reverseForCompare,
      wPx,
      hPx
    );

    const dataW = whiteImageData.data;
    const dataR = reverseImageData.data;
    if (dataW.length !== dataR.length) {
      throw new Error(
        'compareReverseLayerAgainstWhiteInk: ImageData 长度不一致'
      );
    }

    const buildCheckResult = function (
      mismatchCount,
      baseOpaqueCount,
      opaqueCountField
    ) {
      let ratio = 0;
      if (baseOpaqueCount > 0) {
        ratio = mismatchCount / baseOpaqueCount;
      }

      let blocked = false;
      if (mismatchCount > pixelCountThreshold) {
        blocked = true;
      }
      if (baseOpaqueCount > 0 && ratio > ratioThreshold) {
        blocked = true;
      }

      const result = {
        mismatchCount: mismatchCount,
        ratio: ratio,
        blocked: blocked
      };
      result[opaqueCountField] = baseOpaqueCount;
      return result;
    };

    let reverseMismatchCount = 0;
    let reverseOpaqueCount = 0;
    let whiteMismatchCount = 0;
    let whiteOpaqueCount = 0;
    // 单次扫描同时统计两个方向，避免重复遍历 ImageData。
    for (let i = 0; i < dataR.length; i += 4) {
      const aw = dataW[i + 3];
      const ar = dataR[i + 3];

      if (ar !== 0) {
        reverseOpaqueCount++;
        if (aw === 0) {
          reverseMismatchCount++;
        }
      }
      if (aw !== 0) {
        whiteOpaqueCount++;
        if (ar === 0) {
          whiteMismatchCount++;
        }
      }
    }

    // 以反面非透明像素为基准，检查对应位置是否缺少白墨覆盖。
    const reverseWithoutWhite = buildCheckResult(
      reverseMismatchCount,
      reverseOpaqueCount,
      'reverseOpaqueCount'
    );
    // 以白墨非透明像素为基准，检查对应位置是否缺少反面图内容。
    const whiteWithoutReverse = buildCheckResult(
      whiteMismatchCount,
      whiteOpaqueCount,
      'whiteOpaqueCount'
    );

    const result = {
      blocked: reverseWithoutWhite.blocked || whiteWithoutReverse.blocked,
      reverseWithoutWhite: reverseWithoutWhite,
      whiteWithoutReverse: whiteWithoutReverse
    };
    if (debug) {
      result.debugWhiteInkDataUrl = whiteForCompare || '';
      result.debugReverseDataUrl = reverseForCompare;
    }
    return result;
  } catch (error) {
    console.error('compareReverseLayerAgainstWhiteInk: 处理失败', error);
    const out = createEmptyResult();
    out.error = error;
    out.blocked = false;
    return out;
  }
}

/**
 * 获取"白墨导出按 imageUrl 透明区裁剪"的触发信息
 * 触发条件（按需求定义）：
 * - 存在白墨图层（仅考虑 layer_template 与 layer_userImage，且 whiteInk != emptyWhiteInk）
 * - layer_template 的 zIndex 大于所有 layer_userImage 的 zIndex
 * - layer_template 存在非空 imageUrl
 *
 * @param {Array} layers - layer 数组
 * @returns {{imageUrl: string}|null}
 */
function getWhiteInkMaskTransparentCropInfo(layers) {
  if (!layers || !Array.isArray(layers) || layers.length === 0) {
    return null;
  }

  // 1) 判断是否存在“有效白墨图层”（按 drawWhiteInkOnly 口径）
  let hasValidWhiteInkLayer = false;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (
      layer &&
      (layer.type === constant.layer_userImage ||
        layer.type === constant.layer_template) &&
      layer.whiteInk != emptyWhiteInk &&
      !noWhiteLayerType.includes(layer.type)
    ) {
      hasValidWhiteInkLayer = true;
      break;
    }
  }
  if (!hasValidWhiteInkLayer) {
    return null;
  }

  // 2) 找到模板层
  let templateLayer = null;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && layer.type === constant.layer_template) {
      templateLayer = layer;
      break;
    }
  }
  if (!templateLayer || typeof templateLayer.zIndex !== 'number') {
    return null;
  }

  const imageUrl = templateLayer.imageUrl || '';
  if (!imageUrl) {
    return null;
  }

  // 3) 仅对比 userImage：模板 zIndex 必须大于所有 userImage 的 zIndex
  let maxUserImageZIndex = null;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && layer.type === constant.layer_userImage) {
      const z = typeof layer.zIndex === 'number' ? layer.zIndex : 0;
      if (maxUserImageZIndex === null || z > maxUserImageZIndex) {
        maxUserImageZIndex = z;
      }
    }
  }
  // 没有 userImage：按需求描述不触发
  if (maxUserImageZIndex === null) {
    return null;
  }
  if (!(templateLayer.zIndex > maxUserImageZIndex)) {
    return null;
  }

  return {
    imageUrl,
    whiteInk: templateLayer.whiteInk
  };
}

/**
 * 对白墨导出图按 imageUrl 的透明区域裁剪（仅透明区可见）
 * 用户的白墨只影响显示区域,不会对type=template的区域(非透明区域)造车影响
 * - imageUrl 按画布尺寸拉伸铺满
 * - 判定透明区域：mask alpha < threshold
 *
 * @param {Object} options
 * @param {string} options.whiteInkImageDataURL - 白墨导出图（base64/dataURL）
 * @param {string} options.imageUrl - 模板 imageUrl
 * @param {number} options.canvasWidth - 画布逻辑宽
 * @param {number} options.canvasHeight - 画布逻辑高
 * @param {number} [options.threshold=16] alpha 阈值（0-255），小于阈值视为透明区域
 * @returns {Promise<string>} 裁剪后的 dataURL
 */
async function cropWhiteInkByMaskTransparentArea(options) {
  const {
    whiteInkImageDataURL,
    imageUrl,
    canvasWidth,
    canvasHeight,
    threshold = 16,
    whiteInk
  } = options || {};

  if (!whiteInkImageDataURL || typeof whiteInkImageDataURL !== 'string') {
    throw new Error(
      'cropWhiteInkByMaskTransparentArea: whiteInkImageDataURL 必须是非空字符串'
    );
  }
  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new Error(
      'cropWhiteInkByMaskTransparentArea: imageUrl 必须是非空字符串'
    );
  }
  if (
    whiteInk !== undefined &&
    whiteInk !== null &&
    typeof whiteInk !== 'string'
  ) {
    throw new Error('cropWhiteInkByMaskTransparentArea: whiteInk 类型无效');
  }
  if (
    typeof canvasWidth !== 'number' ||
    canvasWidth <= 0 ||
    typeof canvasHeight !== 'number' ||
    canvasHeight <= 0
  ) {
    throw new Error(
      'cropWhiteInkByMaskTransparentArea: canvasWidth/canvasHeight 无效'
    );
  }

  const t = typeof threshold === 'number' ? threshold : 16;

  const dpr = window.devicePixelRatio || 1;
  const actualWidth = Math.floor(canvasWidth * dpr);
  const actualHeight = Math.floor(canvasHeight * dpr);

  const shouldOverlayTemplateWhiteInk =
    !!whiteInk && typeof whiteInk === 'string' && whiteInk != emptyWhiteInk;

  const loadPromises = [
    canvasUtil.loadImage(whiteInkImageDataURL),
    canvasUtil.loadImage(imageUrl)
  ];
  if (shouldOverlayTemplateWhiteInk) {
    loadPromises.push(canvasUtil.loadImage(whiteInk));
  }

  const loadedImgs = await Promise.all(loadPromises);
  const whiteInkImg = loadedImgs[0];
  const maskImg = loadedImgs[1];

  canvasUtil.checkMaskProportion(
    maskImg.width,
    maskImg.height,
    canvasWidth,
    canvasHeight,
    'cropWhiteInkByMaskTransparentArea'
  );

  const templateWhiteInkImg = loadedImgs.length > 2 ? loadedImgs[2] : null;
  mwx.debugLog({
    title: 'cropWhiteInkByMaskTransparentArea',
    whiteInkImageDataURL,
    imageUrl,
    whiteInk
  });

  const whiteInkCanvas = document.createElement('canvas');
  const maskCanvas = document.createElement('canvas');
  const resultCanvas = document.createElement('canvas');

  const whiteInkInit = await canvasUtil.initCanvas({
    dom: whiteInkCanvas,
    canvasWidth,
    canvasHeight
  });
  const maskInit = await canvasUtil.initCanvas({
    dom: maskCanvas,
    canvasWidth,
    canvasHeight
  });
  const resultInit = await canvasUtil.initCanvas({
    dom: resultCanvas,
    canvasWidth,
    canvasHeight
  });

  const whiteInkCtx = whiteInkInit && whiteInkInit.ctx;
  const maskCtx = maskInit && maskInit.ctx;
  const resultCtx = resultInit && resultInit.ctx;
  const whiteInkNode = whiteInkInit && whiteInkInit.canvasNode;
  const maskNode = maskInit && maskInit.canvasNode;
  const resultNode = resultInit && resultInit.canvasNode;

  if (!whiteInkCtx || !maskCtx || !resultCtx) {
    throw new Error(
      'cropWhiteInkByMaskTransparentArea: 离屏 canvas 初始化失败'
    );
  }

  try {
    // 白墨图：按画布尺寸拉伸铺满（其导出本身就是全画布坐标系）
    whiteInkCtx.clearRect(0, 0, canvasWidth, canvasHeight);
    whiteInkCtx.drawImage(whiteInkImg, 0, 0, canvasWidth, canvasHeight);

    // mask：按画布尺寸拉伸铺满
    maskCtx.clearRect(0, 0, canvasWidth, canvasHeight);
    maskCtx.drawImage(maskImg, 0, 0, canvasWidth, canvasHeight);

    const whiteInkImageData = whiteInkCtx.getImageData(
      0,
      0,
      actualWidth,
      actualHeight
    );
    const maskImageData = maskCtx.getImageData(0, 0, actualWidth, actualHeight);

    const whiteInkData = whiteInkImageData.data;
    const maskData = maskImageData.data;

    const resultImageData = resultCtx.createImageData(
      actualWidth,
      actualHeight
    );
    const resultData = resultImageData.data;

    for (let i = 0; i < resultData.length; i += 4) {
      const maskA = maskData[i + 3];
      if (maskA < t) {
        // 透明区：保留白墨
        resultData[i] = whiteInkData[i];
        resultData[i + 1] = whiteInkData[i + 1];
        resultData[i + 2] = whiteInkData[i + 2];
        resultData[i + 3] = whiteInkData[i + 3];
      } else {
        // 非透明区：裁掉（透明）
        resultData[i] = whiteInkData[i];
        resultData[i + 1] = whiteInkData[i + 1];
        resultData[i + 2] = whiteInkData[i + 2];
        resultData[i + 3] = 0;
      }
    }

    resultCtx.putImageData(resultImageData, 0, 0);

    // 叠加模板白墨（template 白墨不参与裁剪）
    if (templateWhiteInkImg && shouldOverlayTemplateWhiteInk) {
      resultCtx.drawImage(templateWhiteInkImg, 0, 0, canvasWidth, canvasHeight);
    }

    return resultNode.toDataURL('image/png');
  } finally {
    // 释放离屏资源
    if (whiteInkNode) {
      whiteInkNode.width = 0;
      whiteInkNode.height = 0;
    }
    if (maskNode) {
      maskNode.width = 0;
      maskNode.height = 0;
    }
    if (resultNode) {
      resultNode.width = 0;
      resultNode.height = 0;
    }
  }
}

/**
 * 应用 skuTemplate 的 maskUrl 蒙版裁剪到 Fabric canvas
 * - 若存在 type=skuTemplate 且 maskUrl 非空：黑色区域裁剪为透明
 * - 若不存在或 maskUrl 为空：清空 fabricCanvas.clipPath，避免残留影响后续绘制
 *
 * 说明：
 * - 蒙版图片按画布尺寸直接拉伸铺满（0,0,canvasW,canvasH）
 * - 黑色阈值：亮度 < threshold 且 alpha > 0 时判定为“需要裁剪掉”
 * - 使用 fabricCanvas.clipPath 保留原有对象（不做扁平化）
 *
 * @param {Object} options
 * @param {fabric.Canvas} options.fabricCanvas
 * @param {Array} options.layers
 * @param {number} [options.threshold=16] 黑色亮度阈值（0-255）
 */
export async function applySkuTemplateMaskClipPath(options) {
  const { fabricCanvas, layers, threshold = 10, bgColor } = options || {};

  if (!fabricCanvas) {
    throw new Error('applySkuTemplateMaskClipPath: fabricCanvas 参数不能为空');
  }
  if (!layers || !Array.isArray(layers)) {
    // 允许调用方在某些场景传空，直接清理即可
    fabricCanvas.clipPath = null;
    fabricCanvas.renderAll();
    return;
  }

  // 找到 skuTemplate layer
  let skuTemplateLayer = null;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && layer.type === constant.layer_template) {
      skuTemplateLayer = layer;
      break;
    }
  }

  // maskUrl: 裁剪区域为透明的,其余部分为黑色
  const maskUrl =
    skuTemplateLayer && typeof skuTemplateLayer.maskUrl === 'string'
      ? skuTemplateLayer.maskUrl
      : options.maskUrl || '';

  // 无 mask：清理裁剪
  if (!maskUrl) {
    if (fabricCanvas.clipPath) {
      fabricCanvas.clipPath = null;
      fabricCanvas.renderAll();
    }
    return;
  }

  try {
    const canvasWidth =
      typeof fabricCanvas.getWidth === 'function'
        ? fabricCanvas.getWidth()
        : fabricCanvas.width;
    const canvasHeight =
      typeof fabricCanvas.getHeight === 'function'
        ? fabricCanvas.getHeight()
        : fabricCanvas.height;

    if (
      typeof canvasWidth !== 'number' ||
      canvasWidth <= 0 ||
      typeof canvasHeight !== 'number' ||
      canvasHeight <= 0
    ) {
      throw new Error(
        'applySkuTemplateMaskClipPath: canvasWidth/canvasHeight 无效'
      );
    }

    // 生成 alpha 蒙版（黑色区域 alpha=0，其它 alpha=255）
    const maskImg = await canvasUtil.loadImage(maskUrl);

    canvasUtil.checkMaskProportion(
      maskImg.width,
      maskImg.height,
      canvasWidth,
      canvasHeight,
      'applySkuTemplateMaskClipPath'
    );

    const offscreenCanvas = document.createElement('canvas');
    const initRes = await canvasUtil.initCanvas({
      dom: offscreenCanvas,
      canvasWidth,
      canvasHeight
    });
    const ctx = initRes && initRes.ctx;
    const canvasNode = initRes && initRes.canvasNode;

    if (!ctx || !canvasNode) {
      throw new Error('applySkuTemplateMaskClipPath: 离屏 canvas 初始化失败');
    }

    // 按画布尺寸拉伸铺满
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.drawImage(maskImg, 0, 0, canvasWidth, canvasHeight);

    // initCanvas 会按 dpr 扩大真实像素尺寸
    const dpr = window.devicePixelRatio || 1;
    const actualWidth = Math.floor(canvasWidth * dpr);
    const actualHeight = Math.floor(canvasHeight * dpr);

    const imageData = ctx.getImageData(0, 0, actualWidth, actualHeight);
    const data = imageData.data;
    const outImageData = ctx.createImageData(actualWidth, actualHeight);
    const outData = outImageData.data;

    const t = typeof threshold === 'number' ? threshold : 16;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      const brightness = Math.floor((r + g + b) / 3);
      // 规则：
      // - mask 透明像素(alpha=0)：视为保留（不裁剪）
      // - mask 非透明像素且接近黑色：裁剪（透明）
      const isBlack = a > 10 && brightness < t;

      // clipPath 使用 alpha 判断；白色不透明、黑色透明
      outData[i] = 255;
      outData[i + 1] = 255;
      outData[i + 2] = 255;
      outData[i + 3] = isBlack ? 0 : 255;
    }

    // putImageData 不受 transform 影响，直接写入实际像素
    ctx.putImageData(outImageData, 0, 0);

    const maskDataUrl = canvasNode.toDataURL('image/png');

    // Fabric 裁剪图（注意 retinaScaling）
    let retinaScaling = 1;
    if (typeof fabricCanvas.getRetinaScaling === 'function') {
      retinaScaling = fabricCanvas.getRetinaScaling();
    } else {
      retinaScaling = dpr;
    }
    if (!retinaScaling || retinaScaling <= 0) {
      retinaScaling = 1;
    }

    const clipImg = await FabricImage.fromURL(maskDataUrl, {
      crossOrigin: 'anonymous'
    });

    clipImg.set({
      left: 0,
      top: 0,
      originX: 'left',
      originY: 'top',
      scaleX: 1 / retinaScaling,
      scaleY: 1 / retinaScaling,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      evented: false
    });
    clipImg.absolutePositioned = true;

    fabricCanvas.clipPath = clipImg;
    fabricCanvas.renderAll();

    // 释放离屏资源
    canvasNode.width = 0;
    canvasNode.height = 0;
  } catch (error) {
    console.error('applySkuTemplateMaskClipPath: 处理失败', error);
    throw error;
  }
}

/**
 * 将 dataURL 铺满 fabricCanvas（与 replaceCanvasWithImage 同缩放口径）
 * @param {fabric.StaticCanvas|fabric.Canvas} fabricCanvas
 * @param {string} dataURL
 * @returns {Promise<void>}
 */
async function replaceFabricCanvasWithDataURLImage(fabricCanvas, dataURL) {
  if (!fabricCanvas || !dataURL) {
    return;
  }
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;
  const img = await FabricImage.fromURL(dataURL, {
    crossOrigin: 'anonymous'
  });
  fabricCanvas.clear();
  fabricCanvas.clipPath = null;
  img.set({
    left: 0,
    top: 0,
    originX: 'left',
    originY: 'top',
    selectable: false,
    evented: false,
    hasControls: false,
    hasBorders: false
  });
  if (img.width && img.height) {
    img.scaleX = canvasW / img.width;
    img.scaleY = canvasH / img.height;
  }
  fabricCanvas.add(img);
  fabricCanvas.renderAll();
}

/**
 * 对已导出的 PNG dataURL 按画布逻辑尺寸铺回离屏 Fabric，再套用与编辑页一致的 sku mask 裁剪（applySkuTemplateMaskClipPath），重新导出。
 * 无 maskUrl 或 excuteMaskClip 为 false 时原样返回 dataURL。
 *
 * @param {Object} options
 * @param {string} options.dataURL
 * @param {Object} options.canvasInfo - displayWidth / displayHeight
 * @param {string} [options.maskUrl]
 * @param {Array} [options.layers] - 解析 mask 的兜底（通常与模板层一致）
 * @param {string} [options.bgColor]
 * @param {number} [options.highResolutionMultiplier=1]
 * @param {boolean} [options.excuteMaskClip=true]
 * @returns {Promise<string>}
 */
export async function applySkuMaskClipToDataURL(options) {
  const opts = options || {};
  const dataURL = opts.dataURL;
  const canvasInfo = opts.canvasInfo;
  const maskUrl =
    opts.maskUrl && typeof opts.maskUrl === 'string' ? opts.maskUrl : '';
  const layers = opts.layers;
  const highResolutionMultiplier =
    typeof opts.highResolutionMultiplier === 'number'
      ? opts.highResolutionMultiplier
      : 1;
  const excuteMaskClip = opts.excuteMaskClip !== false;

  if (!excuteMaskClip || !maskUrl) {
    return dataURL || '';
  }
  if (!dataURL || !canvasInfo) {
    return dataURL || '';
  }
  if (
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    return dataURL;
  }

  const fabricCanvas = createOffscreenStaticCanvas({
    width: canvasInfo.displayWidth,
    height: canvasInfo.displayHeight
  });
  await replaceFabricCanvasWithDataURLImage(fabricCanvas, dataURL);
  await applySkuTemplateMaskClipPath({
    fabricCanvas: fabricCanvas,
    layers: Array.isArray(layers) ? layers : [],
    maskUrl: maskUrl,
    bgColor: opts.bgColor
  });
  const out = fabricCanvas.toDataURL({
    format: 'png',
    quality: 1,
    multiplier: highResolutionMultiplier
  });
  return out;
}

/**
 * 使用 globalCompositeOperation 对导出的 fabricCanvas 图片进行反向蒙版裁剪
 *
 * 适用场景：
 * - 需要“扁平化”导出一张已裁剪后的 PNG（不依赖 Fabric 的 clipPath 实时裁剪）
 * - 直接使用 maskUrl 的 alpha 通道作为裁剪蒙版
 *
 * 规则：
 * - maskUrl 透明区域：保留 fabricCanvas 对应位置的内容
 * - maskUrl 不透明区域：擦除 fabricCanvas 对应位置的内容
 *
 * @param {Object} options - 配置选项
 * @param {fabric.Canvas} options.fabricCanvas - Fabric Canvas 对象
 * @param {string} options.maskUrl - 蒙版图片 URL
 * @returns {Promise<string>} 返回裁剪后的 PNG DataURL（无 maskUrl 时直接返回导出结果）
 *
 * @example
 * const outPng = await applySkuTemplateMaskByComposite({
 *   fabricCanvas,
 *   maskUrl: getMaskUrl()
 * });
 */
export async function applySkuTemplateMaskByComposite(options) {
  const { fabricCanvas, maskUrl } = options || {};

  if (!fabricCanvas) {
    throw new Error(
      'applySkuTemplateMaskByComposite: fabricCanvas 参数不能为空'
    );
  }

  // 先导出 Fabric canvas
  const exportDataUrl = fabricCanvas.toDataURL({
    format: 'png',
    quality: 1
  });

  // 无 maskUrl：直接返回导出结果
  if (!maskUrl || typeof maskUrl !== 'string') {
    return exportDataUrl;
  }

  try {
    // 1) 加载导出的 Fabric 图片（图片 A）
    const srcImg = await canvasUtil.loadImage(exportDataUrl);
    const srcW =
      (srcImg && srcImg.naturalWidth) || (srcImg && srcImg.width) || 0;
    const srcH =
      (srcImg && srcImg.naturalHeight) || (srcImg && srcImg.height) || 0;
    if (!srcW || !srcH) {
      throw new Error('applySkuTemplateMaskByComposite: 导出图片尺寸获取失败');
    }

    // 2) 加载 maskUrl
    const maskImg = await canvasUtil.loadImage(maskUrl);

    canvasUtil.checkMaskProportion(
      maskImg.width,
      maskImg.height,
      srcW,
      srcH,
      'applySkuTemplateMaskByComposite'
    );

    // 3) 创建输出 canvas 并使用 destination-out 合成
    const outCanvas = document.createElement('canvas');
    outCanvas.width = srcW;
    outCanvas.height = srcH;
    const outCtx = outCanvas.getContext('2d');
    if (!outCtx) {
      throw new Error('applySkuTemplateMaskByComposite: outCanvas 初始化失败');
    }

    // 先绘制图片 A
    outCtx.clearRect(0, 0, srcW, srcH);
    outCtx.drawImage(srcImg, 0, 0, srcW, srcH);

    // 使用 destination-out：maskUrl 不透明区域会擦除图片 A
    outCtx.globalCompositeOperation = 'destination-out';
    outCtx.drawImage(maskImg, 0, 0, srcW, srcH);

    // 恢复默认合成模式
    outCtx.globalCompositeOperation = 'source-over';

    const outDataUrl = outCanvas.toDataURL('image/png');

    // 释放离屏资源
    outCanvas.width = 0;
    outCanvas.height = 0;

    return outDataUrl;
  } catch (error) {
    console.error('applySkuTemplateMaskByComposite: 处理失败', error);
    throw error;
  }
}

/**
 * 从 Fabric 对象提取 layerInfo
 * @param {fabric.Object} fabricObj - Fabric 对象
 * @returns {Object} layerInfo 对象
 */
export function extractLayerInfoFromFabricObject(fabricObj) {
  if (!fabricObj) {
    throw new Error('extractLayerInfoFromFabricObject: fabricObj 参数不能为空');
  }

  // layerInfo.width/height 就是图片原始宽高（fabricObj.width/height）
  // 直接提取 Fabric 对象的属性
  const layerInfo = {
    left: fabricObj.left || 0,
    top: fabricObj.top || 0,
    width: fabricObj.width || 0,
    height: fabricObj.height || 0,
    scaleX: fabricObj.scaleX || 1,
    scaleY: fabricObj.scaleY || 1,
    angle: fabricObj.angle || 0,
    originX: fabricObj.originX || 'left',
    originY: fabricObj.originY || 'top'
  };

  return layerInfo;
}

/**
 * 扫描图片四周的透明像素边距（像素级）
 * @param {string} imageUrl - 图片 URL
 * @param {number} imageWidth - 图片宽度
 * @param {number} imageHeight - 图片高度
 * @returns {Promise<{leftWidth: number, rightWidth: number, topWidth: number, bottomWidth: number}>}
 */
export async function scanImageTransparentBorders(
  imageUrl,
  imageWidth,
  imageHeight
) {
  var canvas = document.createElement('canvas');
  var ctx = canvas.getContext('2d');
  canvas.width = imageWidth;
  canvas.height = imageHeight;

  var img = await new Promise(function (resolve, reject) {
    var image = new Image();
    if (/^http/.test(imageUrl)) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = function () {
      resolve(image);
    };
    image.onerror = function (err) {
      reject(err);
    };
    image.src = imageUrl;
  });

  ctx.drawImage(img, 0, 0, imageWidth, imageHeight);
  var data = ctx.getImageData(0, 0, imageWidth, imageHeight);
  var w = data.width;
  var h = data.height;
  var d = data.data;

  function isColTransparent(col) {
    for (var i = 0; i < h; i++) {
      if (d[(i * w + col) * 4 + 3] !== 0) return false;
    }
    return true;
  }
  function isRowTransparent(row) {
    for (var j = 0; j < w; j++) {
      if (d[(row * w + j) * 4 + 3] !== 0) return false;
    }
    return true;
  }

  var leftWidth = 0;
  while (leftWidth < w && isColTransparent(leftWidth)) leftWidth++;

  var rightWidth = 0;
  while (rightWidth < w && isColTransparent(w - 1 - rightWidth)) rightWidth++;

  var topWidth = 0;
  while (topWidth < h && isRowTransparent(topWidth)) topWidth++;

  var bottomWidth = 0;
  while (bottomWidth < h && isRowTransparent(h - 1 - bottomWidth))
    bottomWidth++;

  return {
    leftWidth: leftWidth,
    rightWidth: rightWidth,
    topWidth: topWidth,
    bottomWidth: bottomWidth
  };
}

/**
 * 扫描图片的"非透明像素最小包围盒 bbox"（基于图片像素坐标系，左上为原点）
 *
 * 与 scanImageTransparentBorders 相比：直接返回主体在图片内的 {left, top, width, height}，
 * 调用方常用于"导出图片 → 像素扫描定位主体覆盖范围"的兜底检测场景。
 *
 * @param {string} imageUrl - 图片 URL（支持 PNG/base64/blob，跨域图片需服务端开 CORS）
 * @param {number} imageWidth - 图片宽度（像素）
 * @param {number} imageHeight - 图片高度（像素）
 * @returns {Promise<{left:number, top:number, width:number, height:number}>}
 *   全透明时返回 { left: 0, top: 0, width: 0, height: 0 }
 */
export async function scanImageNonTransparentBBox(
  imageUrl,
  imageWidth,
  imageHeight
) {
  var borders = await scanImageTransparentBorders(
    imageUrl,
    imageWidth,
    imageHeight
  );
  var width = imageWidth - borders.leftWidth - borders.rightWidth;
  var height = imageHeight - borders.topWidth - borders.bottomWidth;
  if (width <= 0 || height <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  return {
    left: borders.leftWidth,
    top: borders.topWidth,
    width: width,
    height: height
  };
}

/**
 * 根据图片 URL 创建 layerInfo
 * @param {Object} options - 配置选项
 * @param {string} options.imageUrl - 图片 URL
 * @param {Object} options.canvasInfo - 画布信息对象，包含 displayWidth 和 displayHeight
 * @param {string} [options.type] - 布局类型：ImageLayoutTypeMap.fit | fill | fitRemoveTransparent
 * @returns {Promise<Object>} layerInfo 对象
 */
export async function createLayerInfoFromImageUrl(options) {
  if (!options) {
    throw new Error('createLayerInfoFromImageUrl: options 参数不能为空');
  }

  const {
    imageUrl,
    canvasInfo,
    type = constant.ImageLayoutTypeMap.fit
  } = options;

  // 使用验证函数验证参数
  validateImageUrl(imageUrl, 'createLayerInfoFromImageUrl: imageUrl');
  validateCanvasInfo(canvasInfo, 'createLayerInfoFromImageUrl: canvasInfo');
  validateLayoutType(type, 'createLayerInfoFromImageUrl: type');

  // 获取图片信息
  const imageInfo = await mwx.getImageInfo(imageUrl);

  if (type === constant.ImageLayoutTypeMap.fitRemoveTransparent) {
    // 扫描图片四周透明边距，以有效内容尺寸做 fit 布局，再修正偏移
    const borders = await scanImageTransparentBorders(
      imageUrl,
      imageInfo.width,
      imageInfo.height
    );
    const cropWidth = imageInfo.width - borders.leftWidth - borders.rightWidth;
    const cropHeight =
      imageInfo.height - borders.topWidth - borders.bottomWidth;

    const fitDrawInfo = calcImageInCanvasPosition({
      imageWidth: cropWidth > 0 ? cropWidth : imageInfo.width,
      imageHeight: cropHeight > 0 ? cropHeight : imageInfo.height,
      canvasWidth: canvasInfo.displayWidth,
      canvasHeight: canvasInfo.displayHeight,
      type: constant.ImageLayoutTypeMap.fit
    });

    const scaleX =
      fitDrawInfo.inCanvasImageWidth /
      (cropWidth > 0 ? cropWidth : imageInfo.width);
    const scaleY =
      fitDrawInfo.inCanvasImageHeight /
      (cropHeight > 0 ? cropHeight : imageInfo.height);

    return createLayerInfo({
      left: fitDrawInfo.currStatus.x - borders.leftWidth * scaleX,
      top: fitDrawInfo.currStatus.y - borders.topWidth * scaleY,
      width: imageInfo.width,
      height: imageInfo.height,
      angle: 0,
      scaleX: scaleX,
      scaleY: scaleY
    });
  }

  // 计算图片在画布中的位置
  const drawCanvasInfo = calcImageInCanvasPosition({
    imageWidth: imageInfo.width,
    imageHeight: imageInfo.height,
    canvasWidth: canvasInfo.displayWidth,
    canvasHeight: canvasInfo.displayHeight,
    type: type
  });

  // 创建 layerInfo 对象
  return createLayerInfo({
    left: drawCanvasInfo.currStatus.x,
    top: drawCanvasInfo.currStatus.y,
    width: imageInfo.width,
    height: imageInfo.height,
    angle: 0,
    scaleX: drawCanvasInfo.inCanvasImageWidth / imageInfo.width,
    scaleY: drawCanvasInfo.inCanvasImageHeight / imageInfo.height
  });
}

/**
 * 计算图片"非透明主体矩形"经过对象 transform(left/top/scaleX/scaleY/angle) 后
 * 在所属画布坐标系下的轴对齐包围盒 AABB
 *
 * 约定：对象 originX='left' / originY='top'（与项目现有编辑页一致）
 *
 * @param {Object} params
 * @param {Object} params.fabricObj - 具备 left/top/scaleX/scaleY/angle/width/height 字段的对象（fabric 对象或等价的 layerInfo）
 * @param {{leftWidth:number,rightWidth:number,topWidth:number,bottomWidth:number}} params.borders - 由 scanImageTransparentBorders 得到的四周透明边距（基于图片原始像素坐标系）
 * @returns {{left:number, top:number, width:number, height:number}} AABB，单位与 fabricObj.left/top 同坐标系
 */
export function calcSubjectAABB(params) {
  if (!params || !params.fabricObj || !params.borders) {
    throw new Error('calcSubjectAABB: 缺少必要参数 fabricObj / borders');
  }
  var obj = params.fabricObj;
  var borders = params.borders;

  var imgW = typeof obj.width === 'number' ? obj.width : 0;
  var imgH = typeof obj.height === 'number' ? obj.height : 0;
  if (imgW <= 0 || imgH <= 0) {
    throw new Error('calcSubjectAABB: fabricObj.width/height 必须为正数');
  }

  var left = typeof obj.left === 'number' ? obj.left : 0;
  var top = typeof obj.top === 'number' ? obj.top : 0;
  var scaleX = typeof obj.scaleX === 'number' ? obj.scaleX : 1;
  var scaleY = typeof obj.scaleY === 'number' ? obj.scaleY : 1;
  var angle = typeof obj.angle === 'number' ? obj.angle : 0;

  var leftWidth = borders.leftWidth || 0;
  var rightWidth = borders.rightWidth || 0;
  var topWidth = borders.topWidth || 0;
  var bottomWidth = borders.bottomWidth || 0;

  var x1 = leftWidth;
  var x2 = imgW - rightWidth;
  var y1 = topWidth;
  var y2 = imgH - bottomWidth;

  var theta = (angle * Math.PI) / 180;
  var cos = Math.cos(theta);
  var sin = Math.sin(theta);

  function toWorld(lx, ly) {
    var sx = lx * scaleX;
    var sy = ly * scaleY;
    return {
      x: left + sx * cos - sy * sin,
      y: top + sx * sin + sy * cos
    };
  }

  var p1 = toWorld(x1, y1);
  var p2 = toWorld(x2, y1);
  var p3 = toWorld(x2, y2);
  var p4 = toWorld(x1, y2);

  var minX = Math.min(p1.x, p2.x, p3.x, p4.x);
  var maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
  var minY = Math.min(p1.y, p2.y, p3.y, p4.y);
  var maxY = Math.max(p1.y, p2.y, p3.y, p4.y);

  return {
    left: minX,
    top: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * 计算"图片非透明主体矩形的 AABB 至少一边 >= 显示区对应边，且该维度完全覆盖显示区"的修正
 *
 * 修正顺序：
 *   1) 若两维 AABB 都 < display 对应边，则以显示区中心为锚点，按 ratio=max(displayW/AABB.w, displayH/AABB.h) 同比放大 scaleX/scaleY；
 *   2) 基于（可能已缩放后的）AABB，按维度独立做最小平移补偿，让满足 >= 条件的维度完全覆盖 display。
 *
 * 注意：
 *   - scaleX/scaleY 放大时同乘一个 ratio，保持二者原比例关系（兼容 pinch 产生的非均匀）；
 *   - 浮点容差 EPS=0.5，避免亚像素抖动导致反复修正；
 *   - 原对象属性不会被修改，仅返回建议的新 left/top/scaleX/scaleY 和 needUpdate 标志。
 *
 * @param {Object} params
 * @param {Object} params.fabricObj - 具备 left/top/scaleX/scaleY/angle/width/height 字段的对象（fabric 对象或等价的 layerInfo）
 * @param {{leftWidth:number,rightWidth:number,topWidth:number,bottomWidth:number}} params.borders - 由 scanImageTransparentBorders 得到的四周透明边距
 * @param {{left:number, top:number, width:number, height:number}} params.displayRect - 显示区矩形（与 fabricObj.left/top 同坐标系）
 * @returns {{needUpdate:boolean, left:number, top:number, scaleX:number, scaleY:number}}
 */
export function calcFitRemoveTransparentCorrection(params) {
  if (!params || !params.fabricObj || !params.borders || !params.displayRect) {
    throw new Error(
      'calcFitRemoveTransparentCorrection: 缺少必要参数 fabricObj / borders / displayRect'
    );
  }
  var obj = params.fabricObj;
  var borders = params.borders;
  var display = params.displayRect;
  var EPS = 0.5;

  var origLeft = typeof obj.left === 'number' ? obj.left : 0;
  var origTop = typeof obj.top === 'number' ? obj.top : 0;
  var origScaleX = typeof obj.scaleX === 'number' ? obj.scaleX : 1;
  var origScaleY = typeof obj.scaleY === 'number' ? obj.scaleY : 1;

  var newLeft = origLeft;
  var newTop = origTop;
  var newScaleX = origScaleX;
  var newScaleY = origScaleY;

  var aabb = calcSubjectAABB({ fabricObj: obj, borders: borders });

  if (aabb.width <= 0 || aabb.height <= 0) {
    return {
      needUpdate: false,
      left: origLeft,
      top: origTop,
      scaleX: origScaleX,
      scaleY: origScaleY
    };
  }

  if (aabb.width + EPS < display.width && aabb.height + EPS < display.height) {
    var ratioW = display.width / aabb.width;
    var ratioH = display.height / aabb.height;
    var bindingAabbSide = ratioW <= ratioH ? aabb.width : aabb.height;
    var ratio = Math.min(ratioW, ratioH) + EPS / bindingAabbSide;
    if (ratio > 1) {
      // 以「主体 AABB 中心」为锚点同比放大，避免主体被强行拉向显示区中心
      // （pinch / 工具按钮缩放后的体感更自然，最终覆盖语义由下方平移补偿保证）
      var acx = aabb.left + aabb.width / 2;
      var acy = aabb.top + aabb.height / 2;
      newScaleX = origScaleX * ratio;
      newScaleY = origScaleY * ratio;
      newLeft = acx - (acx - origLeft) * ratio;
      newTop = acy - (acy - origTop) * ratio;
      aabb = calcSubjectAABB({
        fabricObj: {
          left: newLeft,
          top: newTop,
          scaleX: newScaleX,
          scaleY: newScaleY,
          angle: obj.angle,
          width: obj.width,
          height: obj.height
        },
        borders: borders
      });
    }
  }

  var dx = 0;
  var dy = 0;

  if (aabb.width + EPS >= display.width) {
    if (aabb.left > display.left) {
      dx = display.left - aabb.left;
    } else if (aabb.left + aabb.width < display.left + display.width) {
      dx = display.left + display.width - (aabb.left + aabb.width);
    }
  }
  if (aabb.height + EPS >= display.height) {
    if (aabb.top > display.top) {
      dy = display.top - aabb.top;
    } else if (aabb.top + aabb.height < display.top + display.height) {
      dy = display.top + display.height - (aabb.top + aabb.height);
    }
  }

  newLeft = newLeft + dx;
  newTop = newTop + dy;

  var needUpdate =
    Math.abs(newLeft - origLeft) > 1e-6 ||
    Math.abs(newTop - origTop) > 1e-6 ||
    Math.abs(newScaleX - origScaleX) > 1e-9 ||
    Math.abs(newScaleY - origScaleY) > 1e-9;

  return {
    needUpdate: needUpdate,
    left: newLeft,
    top: newTop,
    scaleX: newScaleX,
    scaleY: newScaleY
  };
}

/**
 * 基于"已知主体 AABB"计算 fabric 对象的覆盖修正（同比放大 + 平移补偿）
 *
 * 与 calcFitRemoveTransparentCorrection 的区别：
 *   - calcFitRemoveTransparentCorrection 内部用 borders + transform 推导 AABB（适合无旋转或矩形主体）
 *   - 本函数 AABB 由调用方提供（例如像素扫描得到的真实主体 bbox），适合有旋转 + 异形主体的兜底场景
 *
 * 算法（与 calcFitRemoveTransparentCorrection 一致）：
 *   1) 若 aabb 两维都 < display 对应边，则以「主体 AABB 中心」为锚点，按 ratio = min(displayW/aabbW, displayH/aabbH) + EPS/binding 同比放大 scaleX/scaleY；
 *   2) 按等比推算放大后的 aabb（中心不变、宽高 × ratio），再按维度独立做最小平移补偿，让满足 >= 条件的维度完全覆盖 display。
 *
 * 注意：
 *   - 不修改入参对象，仅返回建议的新 left/top/scaleX/scaleY 与 needUpdate；
 *   - 浮点容差 EPS=0.5，避免亚像素抖动反复修正。
 *
 * @param {Object} params
 * @param {Object} params.fabricObj - 读取 left/top/scaleX/scaleY（与 displayRect 同坐标系）
 * @param {{left:number,top:number,width:number,height:number}} params.aabb - 主体 AABB（与 displayRect 同坐标系）
 * @param {{left:number,top:number,width:number,height:number}} params.displayRect - 显示区矩形
 * @returns {{needUpdate:boolean,left:number,top:number,scaleX:number,scaleY:number}}
 */
export function calcAabbCoverageCorrection(params) {
  if (!params || !params.fabricObj || !params.aabb || !params.displayRect) {
    throw new Error(
      'calcAabbCoverageCorrection: 缺少必要参数 fabricObj / aabb / displayRect'
    );
  }
  var obj = params.fabricObj;
  var aabb = {
    left: params.aabb.left,
    top: params.aabb.top,
    width: params.aabb.width,
    height: params.aabb.height
  };
  var display = params.displayRect;
  var EPS = 0.5;

  var origLeft = typeof obj.left === 'number' ? obj.left : 0;
  var origTop = typeof obj.top === 'number' ? obj.top : 0;
  var origScaleX = typeof obj.scaleX === 'number' ? obj.scaleX : 1;
  var origScaleY = typeof obj.scaleY === 'number' ? obj.scaleY : 1;

  var newLeft = origLeft;
  var newTop = origTop;
  var newScaleX = origScaleX;
  var newScaleY = origScaleY;

  if (aabb.width <= 0 || aabb.height <= 0) {
    return {
      needUpdate: false,
      left: origLeft,
      top: origTop,
      scaleX: origScaleX,
      scaleY: origScaleY
    };
  }

  if (aabb.width + EPS < display.width && aabb.height + EPS < display.height) {
    var ratioW = display.width / aabb.width;
    var ratioH = display.height / aabb.height;
    var bindingAabbSide = ratioW <= ratioH ? aabb.width : aabb.height;
    var ratio = Math.min(ratioW, ratioH) + EPS / bindingAabbSide;
    if (ratio > 1) {
      // 以「主体 AABB 中心」为锚点同比放大，主体视觉上原地放大，
      // 最终覆盖语义由下方平移补偿保证
      var acx = aabb.left + aabb.width / 2;
      var acy = aabb.top + aabb.height / 2;
      newScaleX = origScaleX * ratio;
      newScaleY = origScaleY * ratio;
      newLeft = acx - (acx - origLeft) * ratio;
      newTop = acy - (acy - origTop) * ratio;
      // 等比推算放大后的 aabb：中心不变，宽高 × ratio
      var newAabbW = aabb.width * ratio;
      var newAabbH = aabb.height * ratio;
      aabb = {
        left: acx - newAabbW / 2,
        top: acy - newAabbH / 2,
        width: newAabbW,
        height: newAabbH
      };
    }
  }

  var dx = 0;
  var dy = 0;

  if (aabb.width + EPS >= display.width) {
    if (aabb.left > display.left) {
      dx = display.left - aabb.left;
    } else if (aabb.left + aabb.width < display.left + display.width) {
      dx = display.left + display.width - (aabb.left + aabb.width);
    }
  }
  if (aabb.height + EPS >= display.height) {
    if (aabb.top > display.top) {
      dy = display.top - aabb.top;
    } else if (aabb.top + aabb.height < display.top + display.height) {
      dy = display.top + display.height - (aabb.top + aabb.height);
    }
  }

  newLeft = newLeft + dx;
  newTop = newTop + dy;

  var needUpdate =
    Math.abs(newLeft - origLeft) > 1e-6 ||
    Math.abs(newTop - origTop) > 1e-6 ||
    Math.abs(newScaleX - origScaleX) > 1e-9 ||
    Math.abs(newScaleY - origScaleY) > 1e-9;

  return {
    needUpdate: needUpdate,
    left: newLeft,
    top: newTop,
    scaleX: newScaleX,
    scaleY: newScaleY
  };
}

/**
 * 获取当前的合成图片（目前返回 previewCompositeImage）
 * 后续可以根据功能返回 outlineImage（包含描边）或 accessoryAndOutlineImage（包含配件）
 *
 * @param {Object} layerData - 完整的图层数据
 * @param {Array} layerData.layers - 图层数组
 * @param {Object} layerData.canvas - 画布信息
 * @returns {Promise<Object>} 返回合成图片对象 { imageSrc, fileName, layerInfo }
 */
export async function getCurrentCompositeImage(layerData) {
  // 目前直接返回 previewCompositeImage
  // 后续可以根据配置返回其他合成图片
  return await generatePreviewCompositeImage(layerData);
}

/**
 * 生成预览合成原始图（previewCompositeImage）
 * 合成所有 type=userImage 和 type=material 的图层
 * 不包含 type=skuTemplate、描边、配件
 *
 * @param {Object} layerData - 完整的图层数据
 * @param {Array} layerData.layers - 图层数组
 * @param {Object} layerData.canvas - 画布信息
 * @returns {Promise<Object>} 返回 previewCompositeImage 对象
 *
 * @example
 * const previewCompositeImage = await generatePreviewCompositeImage(layerData);
 * // 返回:
 * // {
 * //   imageSrc: 'data:image/png;base64,...',
 * //   fileName: 'preview_composite_xxx.png',
 * //   layerInfo: { left: 0, top: 0, width: 750, height: 1000, ... }
 * // }
 */
export async function generatePreviewCompositeImage(layerData) {
  if (!layerData || !layerData.layers || !layerData.canvas) {
    throw new Error('generatePreviewCompositeImage: layerData 参数不完整');
  }

  const { layers, canvas } = layerData;

  // 1. 筛选需要合成的图层（userImage + material + skuTemplate ）
  let layerTypeList = [
    constant.layer_userImage,
    constant.layer_skuMaterial,
    constant.layer_template
  ];
  const compositeLayers = layers.filter((layer) =>
    layerTypeList.includes(layer.type)
  );

  // 2. 按 zIndex 排序
  compositeLayers.sort((a, b) => a.zIndex - b.zIndex);

  // 3. 创建临时 canvas
  const tempCanvas = document.createElement('canvas');

  let scale = canvas.productionWidth / canvas.displayWidth;
  tempCanvas.width = canvas.productionWidth;
  tempCanvas.height = canvas.productionHeight;
  const ctx = tempCanvas.getContext('2d');

  // 4. 逐层绘制（使用已有的 drawLayerToCanvas 方法）
  for (const layer of compositeLayers) {
    try {
      await drawLayerToCanvas({
        ctx,
        layer,
        canvasWidth: canvas.displayWidth,
        canvasHeight: canvas.displayHeight,
        scale
      });
    } catch (error) {
      console.error(
        'generatePreviewCompositeImage: 绘制图层失败',
        layer.id,
        error
      );
      // 继续绘制其他图层
    }
  }

  // 5. 导出为 DataURL
  const imageSrc = tempCanvas.toDataURL('image/png');
  const fileName = `preview_composite_${Date.now()}.png`;

  let imageInfo = await loadImage(imageSrc);
  let layerScale = canvas.displayWidth / imageInfo.naturalWidth;
  // 6. 创建 layerInfo（填满画布）
  const layerInfo = createLayerInfo({
    left: 0,
    top: 0,
    width: imageInfo.naturalWidth,
    height: imageInfo.naturalHeight,
    scaleX: layerScale,
    scaleY: layerScale,
    angle: 0,
    originX: 'left',
    originY: 'top'
  });

  return {
    imageSrc,
    fileName,
    layerInfo
  };
}

/**
 * 创建默认的背景图层（填满画布）
 * 与旧方案的 getBgInfo 不同，新方案直接填满画布，不做居中处理
 *
 * @param {string} imageUrl - 背景图片 URL（previewCompositeImage.imageSrc）
 * @param {Object} canvasInfo - 画布信息 { displayWidth, displayHeight }
 * @returns {Object} 返回背景图层对象
 *
 * @example
 * const backgroundLayer = createDefaultBackgroundLayer(
 *   previewCompositeImage.imageSrc,
 *   { displayWidth: 750, displayHeight: 1000 }
 * );
 */
export async function createDefaultBackgroundLayer(imageUrl, canvasInfo) {
  if (!imageUrl) {
    throw new Error('createDefaultBackgroundLayer: imageUrl 参数不能为空');
  }

  if (!canvasInfo || !canvasInfo.displayWidth || !canvasInfo.displayHeight) {
    throw new Error('createDefaultBackgroundLayer: canvasInfo 参数不完整');
  }

  let imageInfo = await loadImage(imageUrl);
  let scale = canvasInfo.displayWidth / imageInfo.naturalWidth;

  return createLayer({
    type: constant.layer_background,
    imageUrl: imageUrl,
    layerInfo: createLayerInfo({
      left: 0,
      top: 0,
      width: imageInfo.naturalWidth,
      height: imageInfo.naturalHeight,
      scaleX: scale,
      scaleY: scale,
      angle: 0,
      originX: 'left',
      originY: 'top'
    }),
    opacity: mwx.getBackgroundDefaultOpacity(), // 默认透明度
    backgroundUIState: {
      activeTab: 'gradient',
      selectedBg: null
    },
    canEditImage: false, // 背景图层不可编辑
    canAdjustLayer: false, // 背景图层不可调整层级
    zIndex: constant.backgroundMaxZIndex
  });
}

/**
 * 生成纯色或渐变背景图片
 * 创建一个填充了指定颜色的 canvas 并导出为 PNG DataURL
 *
 * @param {string} background - CSS 颜色值（纯色或渐变）
 * @param {number} width - 画布宽度
 * @param {number} height - 画布高度
 * @returns {string} PNG DataURL
 *
 * @example
 * const bgDataUrl = createSolidColorImage('#ff0000', 750, 1000);
 * const gradientDataUrl = createSolidColorImage('linear-gradient(to bottom, #ff0000, #0000ff)', 750, 1000);
 */
export function createSolidColorImage(background, width, height) {
  if (!background) {
    throw new Error('createSolidColorImage: background 参数不能为空');
  }

  if (!width || !height) {
    throw new Error('createSolidColorImage: width 和 height 参数不能为空');
  }

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = height;
  const ctx = tempCanvas.getContext('2d');

  // 处理渐变背景
  if (background.includes('gradient')) {
    // 解析渐变字符串并创建渐变对象
    // 例如: 'linear-gradient(to bottom, #ff0000, #0000ff)'
    const gradientMatch = background.match(/linear-gradient\(([^)]+)\)/);
    if (gradientMatch) {
      const parts = gradientMatch[1].split(',').map((s) => s.trim());
      // 简单处理：to bottom 或默认从上到下
      const gradient = ctx.createLinearGradient(0, 0, 0, height);

      // 提取颜色值
      const colors = parts.filter(
        (p) => p.startsWith('#') || p.startsWith('rgb')
      );
      if (colors.length >= 2) {
        gradient.addColorStop(0, colors[0]);
        gradient.addColorStop(1, colors[1]);
        // 支持多个颜色
        if (colors.length > 2) {
          const step = 1 / (colors.length - 1);
          colors.forEach((color, index) => {
            gradient.addColorStop(index * step, color);
          });
        }
      }

      ctx.fillStyle = gradient;
    } else {
      // 如果解析失败，使用纯色
      ctx.fillStyle = background;
    }
  } else {
    // 纯色背景
    ctx.fillStyle = background;
  }

  ctx.fillRect(0, 0, width, height);

  return tempCanvas.toDataURL('image/png');
}

export function isDefaultWhiteInk(layer) {
  return !(layer.whiteInk == emptyWhiteInk || layer.whiteInk);
}

/**
 * 根据 retouchAction 和 retouchType 获取对应的 userSubType
 * @param {string} retouchAction - 修图动作类型
 * @param {string} retouchType - 修图类型
 * @returns {string} 返回对应的 userSubType 值：'ORIGIN' | 'HEAD' | 'BODY'
 */
function getRetouchType(retouchAction, retouchType) {
  if (retouchAction == constant.tool_noRetouch) {
    return constant.subType_origin;
  } else {
    if (retouchType == constant.tool_headRetouch) {
      return constant.subType_head;
    } else {
      return constant.subType_body;
    }
  }
}

/**
 * 将前端数据结构转换为后端数据结构
 * 前端数据结构：{ canvas: {...}, layers: [...], reverseLayer: [], ... }
 * 后端数据结构：{ canvasInfo: {...}, layers: [...] }
 * @param {Object} frontendData - 前端数据结构
 * @returns {Object} 后端数据结构
 */
export function convertFrontendToBackend(frontendData, template) {
  console.log('convertFrontendToBackend', JSON.stringify(frontendData));

  if (!frontendData || typeof frontendData !== 'object') {
    throw new Error('数据格式有误,请删除后重做');
  }

  const frontendCanvas = frontendData.canvas || {};
  const filterFrontendLayers = function (layers) {
    return (layers || []).filter((layer) => {
      // 透明网格底图仅用于前端编辑 UI，不应传给后端
      if (
        layer &&
        (layer.type === constant.layer_transparentGrid ||
          (layer.type == constant.layer_template && layer.isClearTemplate))
      ) {
        return false;
      }
      return true;
    });
  };

  const fontendKeyToBackendMap = {
    canAdjustLayer: 'sortable',
    canEditImage: 'editable'
  };

  // 可以直接从 layer 读取透传的字段
  const directLayerKeys = ['id', 'type', 'zIndex', 'imageUrl', 'backUrl'];

  const convertLayerCollection = function (layers) {
    const frontendLayers = filterFrontendLayers(layers);
    const convertedLayers = frontendLayers.map((layer) => {
      if (!layer || typeof layer !== 'object') {
        return layer;
      }

      const layerInfo = layer.layerInfo || {};
      const directFields = directLayerKeys.reduce(function (acc, key) {
        acc[key] = layer[key];
        return acc;
      }, {});
      let convertedLayer = Object.assign(directFields, {
        canEditImage: layer.canEditImage ? 1 : 0,
        canAdjustLayer: layer.canAdjustLayer ? 1 : 0,
        layerInfo: {
          left: layerInfo.left,
          top: layerInfo.top,
          scale: layerInfo.scaleX,
          angle: layerInfo.angle,
          width: layerInfo.width,
          height: layerInfo.height
        },
        whiteAction: layer.whiteAction || 'DEFAULT',
        // 白墨收缩
        whiteShrink:
          layer.whiteShrink !== undefined
            ? layer.whiteShrink || layer.whiteShrink === undefined
              ? 1
              : 0
            : 1,
        // 逆向层
        frostUrl:
          layer.frostUrl ||
          layer.reversePrimerLayerImageUrl ||
          layer.reversePrimerLayerAction,
        // 局部 UV
        spotUv: layer.spotUv || layer.scopeUv || layer.scopeUvAction,
        strokeWidth: layer.strokeWidth, // 描边宽度
        strokeColor: layer.strokeColor, // 描边颜色
        whiteInk:
          layer.whiteInk == mwx.constant.emptyWhiteInk
            ? constant.WhiteActionMap.EMPTY
            : layer.whiteInk
      });

      switch (layer.type) {
        case constant.layer_background:
          convertedLayer.opacitySlider =
            layer.opacity / mwx.getBackgroundDefaultOpacity();
          convertedLayer.layerExtra = null;
          break;
        case constant.layer_template:
          if (layer.maskUrl !== undefined) {
            convertedLayer.maskUrl = layer.maskUrl || '';
          }
          if (layer.layerExtra) {
            convertedLayer.layerExtra = layer.layerExtra;
          } else {
            convertedLayer.layerExtra = null;
          }
          break;
        case constant.layer_userImage:
          convertedLayer.layerExtra = {
            ...convertedLayer.layerExtra,
            originImage: layer.originImageInfo,
            userSubType:
              getRetouchType(layer.retouchAction, layer.retouchType) || 'BODY'
          };
          break;
        default:
          convertedLayer.layerExtra = null;
          break;
      }
      Object.keys(fontendKeyToBackendMap).forEach((key) => {
        if (convertedLayer[key]) {
          convertedLayer[fontendKeyToBackendMap[key]] = convertedLayer[key];
          delete convertedLayer[key];
        }
      });
      if (convertedLayer.whiteInk == '') {
        // 不传给后台
        delete convertedLayer.whiteInk;
      }

      return convertedLayer;
    });

    let backgroundConvertedLayer = null;
    const backendLayers = [];
    convertedLayers.forEach((convertedLayer) => {
      if (convertedLayer && convertedLayer.type === constant.layer_background) {
        backgroundConvertedLayer = convertedLayer;
      } else {
        backendLayers.push(convertedLayer);
      }
    });

    let background = null;
    if (
      backgroundConvertedLayer &&
      (backgroundConvertedLayer.imageUrl || '').indexOf('blob') == -1
    ) {
      const bgLayerInfo = backgroundConvertedLayer.layerInfo || {};
      background = {
        canEditImage: backgroundConvertedLayer.canEditImage,
        canAdjustLayer: backgroundConvertedLayer.canAdjustLayer,
        imageUrl: backgroundConvertedLayer.imageUrl,
        opacity: backgroundConvertedLayer.opacity,
        opacitySlider: backgroundConvertedLayer.opacitySlider,
        layerInfo: bgLayerInfo
      };
    }

    return {
      background: background,
      layers: sortLayersByZIndex(backendLayers)
    };
  };

  const frontCollection = convertLayerCollection(frontendData.layers);
  const reverseCollection = convertLayerCollection(frontendData.reverseLayer);
  let isReverseLayerEmpty =
    frontendData.reverseLayer &&
    frontendData.reverseLayer.filter((v) => v.type == constant.layer_userImage)
      .length == 0;

  // 正反不同图
  if (template.isFrontBackLayer) {
    let backImg = frontendData.reverseLayerIsMirror
      ? ''
      : reverseCollection && reverseCollection.layers.length > 0
      ? reverseCollection.layers[0].imageUrl
      : 'empty';

    // 不是正反不同图, backImg不能为'empty',改为null
    if (backImg === 'empty') {
      let template = mwx.Store.get(mwx.constant.skuTemplate);
      if (!template.isFrontBackLayer) {
        backImg = null;
      }
    }

    //  先默认只有一张反面图
    frontCollection.layers.some((v) => {
      if (v.type == constant.layer_userImage && backImg !== null) {
        v.backImg = backImg;
        return true;
      }
    });
  }

  return {
    front: {
      displayWidth: frontendCanvas.displayWidth || 0,
      displayHeight: frontendCanvas.displayHeight || 0,
      background: frontCollection.background,
      layers: frontCollection.layers
    },
    back: {
      displayWidth: frontendCanvas.displayWidth || 0,
      displayHeight: frontendCanvas.displayHeight || 0,
      background: reverseCollection?.background,
      layers: reverseCollection?.layers
    }
  };
}

// 目前还不支持二次编辑, 仅简易转换数据
export function convertBackendToFrontEnd(data) {
  return {
    previewUrl: data.previewUrl,
    imageInfo: data.imageInfo
  };
}

/**
 * 将图片铺满 canvas（等比例时等比例缩放，不等比例时拉伸）
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {string} options.imageUrl - 图片 URL
 * @returns {Promise<void>}
 */
export async function drawImageFillCanvas(options) {
  const { fabricCanvas, imageUrl } = options || {};

  if (!fabricCanvas) {
    throw new Error('drawImageFillCanvas: fabricCanvas 参数不能为空');
  }

  if (!imageUrl) {
    return;
  }

  let timeout = null;
  try {
    // 加载图片，添加超时处理
    const loadPromise = FabricImage.fromURL(imageUrl, {
      crossOrigin: 'anonymous'
    });

    const img = await loadPromise;

    // 获取 canvas 尺寸
    const canvasWidth = fabricCanvas.width;
    const canvasHeight = fabricCanvas.height;

    // 获取图片原始尺寸
    const imageWidth = img.width;
    const imageHeight = img.height;

    // 计算宽高比
    const canvasRatio = canvasWidth / canvasHeight;
    const imageRatio = imageWidth / imageHeight;

    // 判断是否等比例（允许小的误差）
    const ratioDiff = Math.abs(canvasRatio - imageRatio);
    const isEqualRatio = ratioDiff < 0.001;

    let scaleX;
    let scaleY;

    if (isEqualRatio) {
      // 等比例：使用统一的缩放比例
      const scale = canvasWidth / imageWidth;
      scaleX = scale;
      scaleY = scale;
    } else {
      // 不等比例：分别计算 scaleX 和 scaleY，允许变形
      scaleX = canvasWidth / imageWidth;
      scaleY = canvasHeight / imageHeight;
    }

    // 设置图片属性
    img.set({
      left: 0,
      top: 0,
      scaleX: scaleX,
      scaleY: scaleY,
      originX: 'left',
      originY: 'top',
      selectable: false,
      hasControls: false,
      hasBorders: false,
      evented: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true
    });

    // 更新对象的交互边界
    img.setCoords();

    // 添加到 canvas 并渲染
    fabricCanvas.add(img);
    fabricCanvas.renderAll();
  } catch (error) {
    console.error('drawImageFillCanvas: 图片加载或绘制失败', error);
    throw error;
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

// 获取maskUrl
export function getLayerMaskUrl(layerData) {
  let maskUrl = '';
  if (layerData) {
    const skuTemplateLayer = getSkuTemplateLayer(layerData);
    if (
      skuTemplateLayer &&
      typeof skuTemplateLayer.maskUrl === 'string' &&
      skuTemplateLayer.maskUrl
    ) {
      maskUrl = skuTemplateLayer.maskUrl;
    }
  }
  if (!maskUrl) {
    maskUrl = layerData.maskUrl || '';
  }
  return maskUrl;
}

export const EDIT_CACHE_TYPE = 'edit';

export const retouchMap = {
  [constant.tool_fullBodyRetouch]: {
    val: 1,
    text: '抠全身'
  },
  [constant.tool_headRetouch]: {
    val: 5,
    text: '抠大头'
  }
};

export let anotherShapeOptions = [
  {
    shapeName: '默认',
    icon: require('@/assets/image/origin_retouch.png'),
    useDefaultRetouch: true,
    selectedTemplateKey: constant.useDefaultRetouch
  }
];

export let shapeRetouchOptions = [
  {
    shapeName: '抠全身',
    icon: require('@/assets/image/shape_fullbody.png'),
    selectedTemplateKey: constant.tool_fullBodyRetouch,
    toolType: constant.tool_fullBodyRetouch
  },
  {
    shapeName: '抠大头',
    icon: require('@/assets/image/shape_head.png'),
    selectedTemplateKey: constant.tool_headRetouch,
    toolType: constant.tool_headRetouch
  }
];

/** 正反面 layerData 字段：键为正面 canonical，值为该侧真实属性名 */
export const VIEW_LAYER_DATA_FIELDS_FRONT = {
  layers: constant.LayerKeyMap.LAYERS,
  outlineImageSrc: 'outlineImageSrc',
  outlineOriginImageSrc: 'outlineOriginImageSrc',
  outlineOriginImageFileName: 'outlineOriginImageFileName'
};

export const VIEW_LAYER_DATA_FIELDS_REVERSE = {
  layers: constant.LayerKeyMap.REVERSE_LAYER,
  outlineImageSrc: 'reverseOutlineImageSrc',
  outlineOriginImageSrc: 'reverseOutlineOriginImageSrc',
  outlineOriginImageFileName: 'reverseOutlineOriginImageFileName'
};

/**
 * 按正/反视图解析 layerData 上的字段名（键名始终以正面为准）。
 * @param {boolean} isReverseView
 * @param {string} [key] - 与 VIEW_LAYER_DATA_FIELDS_FRONT 的键一致
 * @returns {Object|string|undefined} 无 key 时返回浅拷贝映射对象；有 key 时返回对应字符串
 */
export function getViewLayerDataFields(isReverseView, key) {
  const map = isReverseView
    ? VIEW_LAYER_DATA_FIELDS_REVERSE
    : VIEW_LAYER_DATA_FIELDS_FRONT;
  if (key === undefined) {
    return Object.assign({}, map);
  }
  if (map[key] === undefined) {
    console.error(`getViewLayerDataFields: key ${key} not found in map`, map);
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(`getViewLayerDataFields: key ${key} not found in map`);
    }
    return undefined;
  }
  return map[key];
}
